import type { z } from 'zod';
import type {
  InvoiceCreate as InvoiceCreateSchema,
  InvoiceUpdate as InvoiceUpdateSchema,
} from '@munya/shared';
import type {
  InvoicePublic,
  LeadPublic,
  LeadStage,
  LeadCreate,
  LeadUpdate,
  LeadLegacyWonCreate,
} from '@munya/shared';

// #425 · tipos del abono recibido. Import SOLO de tipos (se borra al compilar): el
// diccionario canónico vive con la aritmética, en modules/contabilidad/credit-notes.ts.
import type {
  AsientoApiLine,
  ExpenseDocumentType,
  RectificationReason,
  RefundStatus,
} from '../modules/contabilidad/credit-notes.js';

// #423 · mapa categoría/proveedor → subcuenta contable del plan de Beragua.
// Dos ejes ORTOGONALES en la categoría: `status` habla de la SUBCUENTA, `lifecycle` de si la
// CATEGORÍA se puede seguir eligiendo en un gasto nuevo.
export type ExpenseCategoryAccountStatus = 'assigned' | 'pending_decision' | 'not_applicable';
export type ExpenseCategoryLifecycle =
  | 'active'
  | 'split'
  | 'withdrawn'
  | 'read_only'
  | 'unclassified';
/** Preguntas abiertas a la gestoría · docs/feedback/2026-07-27_preguntas-abiertas-gestoria.md */
export type ExpenseOpenQuestion = 'A1' | 'A2' | 'A3' | 'A4' | 'A5';

export interface ExpenseCategoryAccount {
  category: string;
  accounting_code: string | null;
  // Todo lo de abajo llega desde #423; se declara opcional para tolerar la respuesta anterior.
  label?: string | null;
  status?: ExpenseCategoryAccountStatus;
  pending_question?: ExpenseOpenQuestion | null;
  lifecycle?: ExpenseCategoryLifecycle;
  /** DERIVADA de lifecycle: sólo `active` se puede elegir en un gasto nuevo. */
  selectable?: boolean;
  /** Categorías que sustituyen a una desglosada (suministros → eléctrico/agua/telefonía). */
  replaced_by?: string[] | null;
  notes?: string | null;
  /** Cuántos gastos usan hoy esta categoría (para no retirarla a ciegas). */
  expense_count?: number;
  updated_at?: string;
}

export type ExpenseSupplierAccountStatus = 'assigned' | 'pending_decision' | 'unreviewed';

export interface ExpenseSupplierAccount {
  id: string;
  display_name: string;
  /** Clave normalizada con la que se casa el nombre que trae la factura. */
  name_key?: string;
  /**
   * 0248 · el NIF va CIFRADO en la base (entre los 188 proveedores del libro hay autónomos, y el
   * NIF de un autónomo es su DNI). El listado NO lo trae: descifrar 188 valores son ~60 s y el
   * pool de la API corta a los 5. Aquí sólo se sabe SI lo tiene; el valor se pide de uno en uno
   * con api.expenseSupplierAccount(id).
   */
  has_nif: boolean;
  category: string | null;
  accounting_code: string | null;
  status: ExpenseSupplierAccountStatus;
  pending_question: ExpenseOpenQuestion | null;
  is_active: boolean;
  source?: string;
  invoices_2025?: number | null;
  base_2025_cents?: number | null;
  retention_2025_cents?: number | null;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

/** 0248 · la ficha de UNA regla, que es lo único que descifra el NIF (null si no lo tiene). */
export type ExpenseSupplierAccountDetail = ExpenseSupplierAccount & { nif: string | null };

// Tipos de ENTRADA (antes de aplicar .default() de Zod) para construir bodies en el cliente.
type InvoiceCreateInput = z.input<typeof InvoiceCreateSchema>;
type InvoiceUpdateInput = z.input<typeof InvoiceUpdateSchema>;

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

// Cliente para el selector de "Nueva factura"
// #422 · `kind` ('particular' | 'company' | 'partner_b2b' | 'public') lo devuelve el endpoint
// desde siempre; se declara aquí para el distintivo Particular/Empresa del combobox. Se tipa
// como string porque el enum de la BD puede crecer sin tocar el front.
// OJO: el listado ya NO trae `phone` ni `legal_rep_nif` (los descifraba pgcrypto con
// s2k-count máximo: 5,5 s de los 6 s del endpoint). Para eso está GET /clients/:id.
export interface ClientOption {
  id: string;
  name: string;
  legal_name: string | null;
  nif: string | null;
  email: string | null;
  kind: string;
}

// #422 · caché de módulo del listado de clientes.
// El endpoint sigue siendo el punto lento de la aplicación (≈0,7 s tras quitar el descifrado
// de phone/legal_rep_nif, ~6 s antes) y el modal de "Nueva factura" lo repetía ENTERO en cada
// apertura. Con esto: TTL de 120 s + deduplicación de peticiones en vuelo (dos componentes que
// montan a la vez comparten una sola petición) + invalidación explícita al crear/editar cliente.
//
// LIMITACIÓN HONESTA: esta caché solo cubre a quien pasa por `api.listClients()`. Hoy eso es
// únicamente InvoiceList.tsx. ContratosSection.tsx, ProjectWizard.tsx y ProjectDetail.tsx
// llaman a `fetch('/api/v1/clients')` en crudo con interfaces locales, así que siguen pagando
// la petición completa cada vez. Migrarlos a api.listClients() es trabajo aparte (cambia el
// tipo de sus estados locales); mientras no se haga, NO se puede asumir que la lista esté
// cacheada en toda la aplicación.
// TTL corto A PROPÓSITO: el backend también crea clientes por su cuenta (cascada post-firma,
// lead ganado legacy, alta desde contrato, ETL de Odoo) y el front no se entera de esas altas,
// así que la invalidación explícita no las cubre. 30 s acota la ventana en la que el flujo
// «gano el lead y facturo acto seguido» podría no ver todavía al cliente nuevo, sin renunciar a
// ahorrar la petición cuando se abre el modal varias veces seguidas.
const CLIENTS_CACHE_TTL_MS = 30_000;
let clientsCache: { key: string; at: number; items: ClientOption[] } | null = null;
let clientsCacheGen = 0;
const clientsInFlight = new Map<string, Promise<{ items: ClientOption[] }>>();

/** Tira la caché del listado de clientes (tras crear o editar un cliente). */
export function invalidateClientsCache(): void {
  // La generación invalida también las respuestas en vuelo: una petición lanzada ANTES del
  // cambio no debe repoblar la caché con datos ya viejos cuando llegue.
  clientsCacheGen += 1;
  clientsCache = null;
  clientsInFlight.clear();
}

// Línea de factura tal como la devuelve GET /invoices/:id
export interface InvoiceLineRow {
  id: string;
  position: number;
  description: string;
  quantity: string;
  unit_price_cents: string;
  discount_pct: string;
  line_subtotal_cents: string;
  vat_rate: string | null; // #162 · IVA por línea
}

// #352 · contrato PATCH /api/v1/leads/:id — datos básicos editables desde la ficha.
// channel es string plano para admitir los nuevos valores del enum lead_channel
// (paid_ads, publicidad · migración 0200) sin acoplarse al z.enum compartido.
export interface LeadBasicUpdate {
  concept_name?: string;
  contact_name?: string;
  contact_email?: string;
  channel?: string;
  typology_intent?: string;
  estimated_area_m2?: number;
  estimated_budget_cents?: number;
  location_description?: string;
  // #444 · la ubicación en tres campos. Si se manda, el servidor recompone
  // `location_description` desde aquí; `null` explícito vacía las dos.
  location_address?: LeadLocationAddressInput | null;
  // #467 · notas de cualificación. Solo viaja si el textarea ha cambiado; cadena vacía (o null)
  // = dejarlas en blanco. El servidor guarda NULL y deja apunte con el texto anterior.
  qualification_notes?: string | null;
}

// #444 punto 8 · qué ha pasado con las DOS carpetas del proyecto. Lo devuelve tanto el alta
// como «Crear carpeta ahora», y la pantalla lo pinta tal cual: si una de las dos falla, se ve
// cuál y por qué en vez de terminar en verde con la referencia vacía.
export interface ProjectFolderTreeOutcome {
  ok: boolean;
  id?: string;
  name?: string;
  web_url?: string | null;
  via?: 'cloned-base' | 'renamed-lead-folder' | 'reused';
  /** La copia de la plantilla sigue en curso en el servidor de OneDrive (pesa 180 MB). */
  pending?: boolean;
  error?: string;
}

export interface ProjectFolderProvision {
  ok: boolean;
  project_id: string;
  skipped?: { reason: string; detail: string };
  studio_number: number | null;
  folder_name: string | null;
  proyectos: ProjectFolderTreeOutcome;
  honcontratos: ProjectFolderTreeOutcome;
  warnings: string[];
  message?: string;
}

// #444 · los tres campos de ubicación que pinta el formulario. La provincia NO se pide: el
// servidor la deduce del código postal y solo respeta la que ya estuviera guardada.
export interface LeadLocationAddressInput {
  street?: string | null;
  postal_code?: string | null;
  city: string;
  province?: string | null;
}

// #79/#428 · Recordatorio de seguimiento de un lead. `effective_notify_*` es el destinatario
// REAL del aviso: notify_user_id si se fijó a mano, si no el responsable del lead (y si tampoco
// hay, Dirección → llega nulo). La burbuja del CRM enseña ese, no el campo vacío.
export interface LeadFollowUp {
  id: string;
  lead_id: string;
  due_date: string;
  note: string;
  status: string;
  notify_user_id: string | null;
  notify_user_name: string | null;
  effective_notify_user_id?: string | null;
  effective_notify_user_name?: string | null;
  notified_at: string | null;
  done_at: string | null;
  created_at: string;
}

// #448 · Videollamada con el lead. Las ETIQUETAS las compone el API (fecha larga, horas,
// duración, origen) para que la ficha y el hub del lead digan exactamente lo mismo: si cada
// pantalla las montara por su cuenta acabarían discrepando en la zona horaria.
export interface LeadMeeting {
  id: string;
  subject: string;
  starts_at: string;
  ends_at: string;
  timezone: string;
  minutes: number;
  date_label: string;
  start_time_label: string;
  end_time_label: string;
  source: 'studio' | 'hub';
  /** «programada por Jorge Moreno» · «reservada por el lead desde el hub». */
  origin_label: string;
  status: 'scheduled' | 'rescheduled' | 'cancelled' | 'held';
  is_current: boolean;
  pinned_current: boolean;
  is_past: boolean;
  join_url: string | null;
  web_link: string | null;
  outlook_event_id: string | null;
  attendees_note: string | null;
  lead_email: string | null;
  /** Veces que se ha movido la cita en TOTAL (estudio + lead). */
  reschedule_count: number;
  /** #454 · veces que la ha movido EL LEAD desde su hub (cupo propio, no lo gasta el estudio). */
  lead_reschedule_count: number;
  /** Cupo de reprogramaciones del lead (2). */
  lead_reschedule_limit: number;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
}

export interface LeadMeetingsResponse {
  items: LeadMeeting[];
  current: LeadMeeting | null;
  /** Hub del lead, para poder AFIRMAR (y enlazar) que la cita se ve ahí. null = no tiene hub. */
  hub: { slug: string; url: string; shows_meeting: boolean } | null;
  count: number;
  previous_count: number;
  /** Duración por defecto del formulario (#448 §8: 45 minutos, no 30). */
  default_minutes: number;
  lead_email: string | null;
}

// C9.3-ter · Certificación de obra (factura con billing_flow=certificacion_obra)
export interface CertificacionRow {
  id: string;
  full_number: string | null;
  status: string;
  certification_sequence: number | null;
  certification_period_start: string | null;
  certification_period_end: string | null;
  subtotal_cents: string;
  tax_cents: string;
  retention_pct: string;
  retention_cents: string;
  retention_released_at: string | null;
  total_cents: string;
  issue_date: string | null;
  created_at: string;
}
export interface RetentionSummary {
  project_id: string;
  retentions_open: number;
  retention_open_cents: string;
  retention_released_cents: string;
  last_retention_period_end: string | null;
}

// TRACK (M4.1)
export type TrackArea =
  | 'carpinteria_interior'
  | 'carpinteria_exterior'
  | 'mobiliario'
  | 'decoracion'
  | 'luminaria'
  | 'instalaciones'
  | 'electrodomesticos'
  | 'domotica'
  | 'intervenciones'
  | 'revestimientos'
  | 'acabados'
  | 'coordinacion'
  | 'administrativo';
export type TrackPriority = 'baja' | 'media' | 'alta' | 'muy_alta';
export type TrackStatus = 'pendiente' | 'en_progreso' | 'en_revision' | 'procesado';
export type TrackVisibility = 'interna' | 'compartido_cliente' | 'compartido_tercero';
export type TrackOrigin = 'equipo' | 'cliente' | 'constructora' | 'proveedor' | 'sistema';

export interface TrackItemRow {
  id: string;
  project_id: string;
  sequence_number: number;
  // #72 · código único global "P{proj_seq}-T{ticket_seq}" (ej. "P6-T42")
  display_code?: string | null;
  project_sequence?: number | null;
  subject: string;
  area: TrackArea;
  priority: TrackPriority;
  status: TrackStatus;
  visibility: TrackVisibility;
  origin: TrackOrigin;
  channel: string;
  assignee_user_id: string | null;
  target_date: string | null;
  sla_hours: number | null;
  breached_at: string | null;
  closed_at: string | null;
  tags: string[];
  project_name: string | null;
  space_label: string | null;
  assignee_name: string | null;
  /** #111 · creador del ticket (display_name interno o email del remitente) */
  creator_name?: string | null;
  // · #453 §5.3 · campos del hilo, para que la LISTA pueda dibujar el árbol de sucesores.
  // Antes no venía ninguno y por eso ninguna vista podía pintarlo.
  parent_item_id?: string | null;
  thread_root_id?: string | null;
  successors_count?: number;
  // · #453 §5.2 · columna de referencias cruzadas de la Lista
  references_count?: number;
  first_reference_identifier?: string | null;
  created_at: string;
  updated_at: string;
}

/** · #453 §5.2 · una referencia cruzada del ticket (artículo de selección o tarea de obra). */
export type TrackRefType = 'selection_item' | 'obra_task';
export interface TrackItemReference {
  id: string;
  ref_type: TrackRefType;
  ref_id: string;
  /** Instantánea del día que se referenció (sobrevive a una reimportación con replace). */
  ref_identifier: string | null;
  ref_label: string | null;
  created_at: string;
  /** Lo que hay HOY al otro lado; null si el destino ya no existe. */
  live_identifier: string | null;
  live_label: string | null;
  space_label: string | null;
  is_alternative: boolean | null;
  client_visibility: string | null;
  alive: boolean;
}

/** · #453 §5.2 · carga ligera del catálogo de selección para el buscador del Track. */
export interface SelectionLightItem {
  id: string;
  identifier: string;
  title: string | null;
  category: string | null;
  space_label: string | null;
  provider_name: string | null;
  is_alternative: boolean;
  client_visibility: string | null;
  thumb_url: string | null;
}

export interface TrackItemDetail extends TrackItemRow {
  description: string | null;
  activity: {
    id: string;
    kind: string;
    actor_user_id: string | null;
    actor_name: string | null;
    actor_email: string | null;
    body: string | null;
    metadata: unknown;
    occurred_at: string;
  }[];
  followers: {
    id: string;
    user_id: string | null;
    follower_email: string | null;
    added_at: string;
  }[];
}

export interface TrackCreate {
  project_id: string;
  subject: string;
  description?: string;
  area?: TrackArea;
  space_id?: string;
  priority?: TrackPriority;
  visibility?: TrackVisibility;
  origin?: TrackOrigin;
  channel?: string;
  assignee_user_id?: string;
  target_date?: string;
  tags?: string[];
  /** · #453 §5.2 · referencias cruzadas ya en el ALTA (el sucesor hereda las del padre). */
  references?: { ref_type: TrackRefType; ref_id: string }[];
}

export interface TrackUpdate {
  subject?: string;
  description?: string;
  area?: TrackArea;
  space_id?: string | null;
  priority?: TrackPriority;
  status?: TrackStatus;
  visibility?: TrackVisibility;
  assignee_user_id?: string | null;
  target_date?: string | null;
  tags?: string[];
  selection_item_id?: string | null; // #277 · referencia cruzada a la selección
}

// Selección (M4.4)
export type SelectionCategory =
  | 'mobiliario'
  | 'luminaria'
  | 'equipamiento'
  | 'decoracion'
  | 'revestimientos_verticales'
  | 'suelos'
  | 'techos'
  | 'carpinteria_interior'
  | 'carpinteria_exterior'
  | 'instalaciones'
  | 'montajes'
  | 'intervenciones'
  | 'paisajismo'
  | 'sanitarios';

export type SelectionOrderStatus =
  | 'pendiente'
  | 'comprado'
  | 'en_fabricacion'
  | 'enviado'
  | 'entrega_confirmada'
  | 'recibido'
  | 'comprobado'
  | 'reclamado';

// Bloque A Selección (2026-06-10) · #115/#92/#93/#100
// · #399 p4 · estado puro: fuera semanas_2_4/semanas_4_8 (migrados en 0222 a
// 'disponible' + shipping_leadtime) y entra 'descatalogado'.
export type SelectionAvailability =
  | 'sin_revisar'
  | 'disponible'
  | 'no_disponible'
  | 'descatalogado';

// · #399 p5 · plazo de envío/entrega del bloque Logística (null = sin plazo)
export type SelectionShippingLeadtime = 'semanas_2_4' | 'semanas_4_8' | 'mas_8_semanas';

// · #411 p3 · unidad de medida (texto con CHECK en 0233, NO enum PG: ampliar la lista = 1
// línea). Lista económica-neutra: solo describe la magnitud del pedido, nunca el precio.
export type SelectionUnit =
  | 'ud'
  | 'm2'
  | 'ml'
  | 'm'
  | 'set'
  | 'pack'
  | 'rollo'
  | 'caja'
  | 'kg'
  | 'l';

export type SelectionClientReview = 'pendiente' | 'aprobado' | 'rechazado' | 'cambio_solicitado';

// · #438 §1 · visibilidad de una posición frente al cliente (enum selection_client_visibility)
export type SelectionClientVisibility = 'visible' | 'oculta';

/** · #438 §1 · lo que NO se pudo cambiar y por qué (una alternativa bajo principal oculta…). */
export interface SelectionVisibilityBlocked {
  id: string;
  identifier: string;
  reason: string;
}

export interface SelectionVisibilityResult {
  updated: number;
  /** Alternativas arrastradas por ocultar su principal. */
  cascaded: number;
  blocked: SelectionVisibilityBlocked[];
}

export interface SelectionInternalApproveResult {
  updated: number;
  blocked: SelectionVisibilityBlocked[];
}

/** · #438 §3 · lo que el modal de «Iniciar revisión» enseña ANTES de enviar nada. */
export interface SelectionStartReviewPreview {
  visible_count: number;
  hidden_count: number;
  visible_principals: number;
  /** Importe en revisión, sin IVA, en céntimos (solo principales visibles). */
  total_client_cents: number;
  problems: {
    id: string;
    identifier: string;
    title: string;
    space_label: string | null;
    problems: ('falta_info' | 'sin_precio')[];
  }[];
  has_open_round: boolean;
}

// · #449 · QUIÉN COMPRA la posición. Eje ORTOGONAL a client_review: «la compro yo» es una
// posición APROBADA por el cliente (client_review='aprobado') que además queda fuera de
// nuestra gestión de compra (purchase_by='cliente'). No sustituye a rechazar ni a pedir
// cambios: el ticket describe el eje de aprobación, no retira las otras respuestas.
export type SelectionPurchaseBy = 'munya' | 'cliente';
export type SelectionExclusionSource = 'portal' | 'estudio';

export interface SelectionPurchaseByResult {
  updated: number;
  /** Alternativas arrastradas por excluir su principal. */
  cascaded: number;
  blocked: SelectionVisibilityBlocked[];
}

/** · #449 §5 · totales separados que la pestaña Selección enseña bajo los KPIs. */
export interface SelectionPurchaseSplit {
  approved_count?: number;
  approved_cents?: number;
  munya_purchase_count?: number;
  munya_purchase_cents?: number;
  client_purchase_count?: number;
  client_purchase_cents?: number;
  /** De las que compra el cliente, las que además están APROBADAS (las que reparten). */
  client_purchase_approved_count?: number;
  client_purchase_approved_cents?: number;
  last_excluded_at?: string | null;
  last_excluded_by?: string | null;
  last_excluded_source?: SelectionExclusionSource | null;
  /** · #470 §7 · piezas que YA SON DEL CLIENTE (principales). Sin importe: no tienen precio. */
  aportadas_count?: number;
  /** · #470 §4 · retiradas propuestas por el estudio y aún sin contestar. */
  retiradas_propuestas_count?: number;
}

// · #470 · DE QUIÉN ES LA PIEZA. Eje propio, ortogonal a los tres que ya existen: `line_kind`
// dice la naturaleza contable, `pricing_status` el estado del precio y `purchase_by` quién
// compra. Una pieza `aportado_cliente` ya está en su casa: no hay compra por delante, no
// lleva importe (sus campos económicos son NULL, no cero) y no suma en ningún total.
export type SelectionProcedencia = 'munya' | 'aportado_cliente';

// · #470 §4 · la propuesta de retirada. NULL = la pieza se queda y nadie ha propuesto nada.
export type SelectionRetiradaEstado = 'propuesta' | 'aceptada' | 'rechazada_por_cliente';

/** · #470 §7 · lo que devuelven las DOS pasadas de /selection/procedencia (previsualizar y aplicar). */
export interface SelectionProcedenciaPlan {
  preview: boolean;
  updated: number;
  targets: {
    id: string;
    identifier: string;
    title: string;
    total_munya_cents: string | null;
    /** true si es una alternativa que, al marcarse como aportada, pasa a principal. */
    promotes_to_principal: boolean;
  }[];
  blocked: SelectionVisibilityBlocked[];
  warnings: string[];
  /** Importe que se retira de los totales al marcarlas como aportadas. */
  cleared_cents: number;
}

// · #399 p11 · 'material' se añade como cuarto tipo de referencia cruzada
export type SelectionLinkKind = 'composicion' | 'tapizado' | 'revestimiento' | 'material';

// #100 · referencia cruzada entre artículos (GET /selection/items/:id/links)
export interface SelectionItemLink {
  id: string;
  link_kind: SelectionLinkKind;
  note: string | null;
  created_at: string;
  from_item_id: string;
  to_item_id: string;
  from_identifier: string;
  from_title: string;
  to_identifier: string;
  to_title: string;
  // · #399 p10 · datos del artículo de cada extremo para la mini-card navegable:
  // miniatura procesada, importe de cliente (total_munya) y enlace web
  from_thumb_url?: string | null;
  to_thumb_url?: string | null;
  from_total_munya_cents?: string | null;
  to_total_munya_cents?: string | null;
  from_provider_url?: string | null;
  to_provider_url?: string | null;
  // · #452 §1 · marca de descuadre: el extremo ya no es el principal de su grupo. La
  // mini-card lo dice y nombra la pieza que SÍ está en el proyecto, en vez de enseñar un
  // artículo perfectamente válido y callar que el proyecto ya no lo lleva.
  from_is_alternative?: boolean;
  to_is_alternative?: boolean;
  from_principal_identifier?: string | null;
  from_principal_title?: string | null;
  to_principal_identifier?: string | null;
  to_principal_title?: string | null;
}

// · #452 §1 · referencia que arrastraría un intercambio (GET …/promote-preview)
// · #474 §2 · un principal candidato a recibir la alternativa que se está moviendo.
export interface SelectionMoveTarget {
  id: string;
  identifier: string;
  title: string;
  space_label: string | null;
  room_group: string | null;
  alternatives: number;
  thumb_url: string | null;
}

// · #476 §2 · cada código que cambia con el traslado (el artículo y las alternativas que viajan).
export interface SelectionMoveCode {
  id: string;
  title: string;
  current_identifier: string;
  next_identifier: string;
  is_origin: boolean;
}

// · #474 §2 · lo que el modal necesita saber ANTES de mover. `blocked` no nulo apaga el botón.
export interface SelectionMovePreview {
  next_identifier: string;
  current_identifier: string;
  /** #476 §2 · la lista COMPLETA, no solo el del artículo movido. */
  codes: SelectionMoveCode[];
  /** true cuando el origen es un principal que se convierte en alternativa. */
  demoting: boolean;
  target_space_label: string | null;
  spaces_differ: boolean;
  warnings: string[];
  blocked: string | null;
}

export interface SelectionRepointCandidate {
  link_id: string;
  link_kind: SelectionLinkKind;
  note: string | null;
  direction: 'entrante' | 'saliente';
  other_item_id: string;
  other_identifier: string;
  other_title: string;
  outcome: 'mover' | 'fusionar' | 'bloqueado';
  blocked_reason: string | null;
}

export interface SelectionRepointSummary {
  moved: number;
  merged: number;
  kept: number;
  blocked: number;
}

// · #452 §3 · catálogos administrables de estancia tipo
export interface RoomGroupCatalogRow {
  code: string;
  label: string;
  position: number;
  active: boolean;
  is_builtin: boolean;
  spaces_count: number;
}
export interface LocationCatalogRow {
  code: string;
  label: string;
  room_group: string | null;
  max_instances: number;
  position: number;
  active: boolean;
  spaces_count: number;
}

export interface SelectionItemComputed {
  id: string;
  identifier: string;
  plan_code: string | null;
  plan_id: string | null;
  project_id: string;
  option_type: 'principal' | 'alternativa';
  line_kind: 'honorarios_internos' | 'producto' | 'honorarios_terceros' | 'suplido';
  validity_days: number | null;
  group_id: string | null;
  sequence_in_group: number;
  category: SelectionCategory;
  room_group: string | null;
  space_id: string | null;
  space_label: string | null;
  provider_id: string | null;
  provider_name: string | null;
  provider_article_ref: string | null;
  provider_url: string | null;
  title: string;
  description: string | null;
  image_url: string | null;
  // #229 · miniatura procesada de la imagen principal (la cruda de OneDrive no renderiza)
  thumb_url?: string | null;
  quantity: string;
  // #128 · S/W/X/Y/AA del Excel · #450 · el backend los envía a quien tiene la ECONOMÍA DEL
  // ARTÍCULO (Dirección, Gestión y Diseño); siguen opcionales porque para el resto no vienen.
  cost_without_vat_cents?: string;
  provider_increment_pct?: string;
  provider_discount_pct?: string;
  /** · #450 · margen del ESTUDIO: viaja solo con el beneficio (Gestión y Dirección). */
  studio_margin_pct?: string;
  // #218/#219 · margen y retención efectivos (derivados) + retención global del proyecto
  effective_margin_pct?: string;
  effective_retention_pct?: string;
  project_dto_retention_pct?: string;
  // · #411 W4a · p5 · DTO aplicado (heredado del catálogo o propio) + flag de fijación manual.
  // · #450 · van con la economía del artículo (Diseño incluido), no con el beneficio.
  effective_discount_pct?: string;
  dto_override?: boolean;
  pricing_status: string;
  order_status: SelectionOrderStatus;
  installation_required: string;
  logistics_mode: string;
  estimated_reception_date: string | null;
  deadline_at_destination?: string | null;
  minimum_destination_date?: string | null;
  scheduled_purchase_date?: string | null;
  estimated_shipping_text?: string | null;
  // #263 · logística · referencias de pedido/factura/suplido
  purchase_order_ref?: string | null;
  provider_invoice_ref?: string | null;
  issued_suplido_number?: string | null;
  // · #470 · NULL en las piezas que ya son del cliente: no tienen precio, y la vista computada
  // propaga el NULL de `cost_without_vat_cents` a los cuatro derivados. `Number(null)` es 0 y
  // suma bien en los totales, pero al PINTAR hay que escribir «—»: «0,00 €» es un precio.
  pvp_unit_cents: string | null;
  munya_unit_cents: string | null;
  total_pvp_cents: string | null;
  total_munya_cents: string | null;
  /** · #128/#450 · BENEFICIO INTERNO: solo Gestión y Dirección; nunca sale al portal. */
  beneficio_cents?: string | null;
  created_at: string;
  // Bloque A (0140) · opcionales: el endpoint puede no devolverlos todavía
  parent_item_id?: string | null;
  parent_identifier?: string | null;
  availability?: SelectionAvailability;
  // · #399 p5/p8 · plazo de envío/entrega y medidas (columnas de 0222)
  shipping_leadtime?: SelectionShippingLeadtime | null;
  measurements_text?: string | null;
  // · #452 p2 · el texto se tecleó con el rótulo antiguo («H x D x L», sin unidad) y está
  // sin revisar. La ficha lo avisa; se apaga solo al reescribir el campo.
  measurements_legacy_order?: boolean;
  // · #411 p3 · unidad de medida (default 'ud'); opcional porque los endpoints antiguos
  // podrían no devolverla
  unit?: SelectionUnit;
  review_status?: 'nuevo' | 'falta_info' | 'alerta' | 'revisado' | 'duda' | null;
  // · #438 §1 · visibilidad de la posición frente al cliente. 'visible' entra en la foto de
  // la ronda; 'oculta' se queda fuera del portal (motivo interno obligatorio). Opcional
  // porque no todos los endpoints la devuelven; ausente ⇒ tratar como visible.
  client_visibility?: SelectionClientVisibility;
  client_hidden_reason?: string | null;
  client_hidden_at?: string | null;
  // · #438 §4 · aprobación INTERNA de Dirección sobre una posición oculta. NO es la
  // aprobación del cliente (client_review) y NO abre suplidos.
  internal_approved_at?: string | null;
  internal_approval_note?: string | null;
  // · #449 §1/§2 · quién compra la posición y la constancia de la exclusión. Ausente ⇒
  // tratar como 'munya' (la compra la gestiona el estudio, que es el caso por defecto).
  purchase_by?: SelectionPurchaseBy;
  purchase_excluded_note?: string | null;
  purchase_excluded_at?: string | null;
  purchase_excluded_source?: SelectionExclusionSource | null;
  purchase_excluded_by_label?: string | null;
  // · #470 · de quién es la pieza. Ausente ⇒ tratar como 'munya' (la especificamos y la
  // gestionamos nosotros, que es el caso por defecto y el de las 3.511 posiciones de hoy).
  // ⚠️ En una `aportado_cliente` los campos económicos llegan a NULL, NO a cero: la pantalla
  // tiene que escribir «—», nunca «0,00 €». Cero es un precio y se suma; la ausencia no.
  procedencia?: SelectionProcedencia;
  // · #470 §4 · propuesta de retirada (solo sobre piezas aportadas)
  retirada_estado?: SelectionRetiradaEstado | null;
  retirada_motivo?: string | null;
  retirada_at?: string | null;
  retirada_respuesta_at?: string | null;
  client_review?: SelectionClientReview;
  client_review_note?: string | null;
  frozen_at?: string | null;
  // #191 · marcado de cambio post-aprobación (gobierna la "Aprobación de cambios")
  change_pending?: boolean;
  change_pending_note?: string | null;
  cross_reference_text?: string | null;
  position?: number | null;
  // · #411 p8 · position de la estancia del artículo (para agrupar + ordenar por espacio en la
  // vista «espacios»); null en «Sin ubicación». Opcional: endpoints antiguos no la devuelven.
  space_position?: number | null;
  // · #411 W5 · p7a · grupo (nombre libre) de la estancia: id + nombre + orden del grupo
  space_group_id?: string | null;
  space_group_name?: string | null;
  space_group_position?: number | null;
  category_group?: string | null;
  category_group_label?: string | null;
  space_room_group?: string | null;
  observations?: string | null;
  internal_notes?: string | null;
  // · #458 · carpeta del proyecto en OneDrive ("026_CASA KLARHEIT_SEVILLA"). Solo la
  // devuelve el GET de un artículo (no el listado): con ella el picker de imágenes abre
  // en la estancia del artículo en vez de en la raíz PROYECTOS.
  project_onedrive_folder_name?: string | null;
}

// · #411 O3 p4 · resultado de duplicar / alta desde catálogo (media_copied/warnings del servidor)
export interface SelectionMutationResult {
  id: string;
  identifier: string;
  media_copied?: number;
  warnings?: string[];
  mode?: 'duplicated' | 'mapped';
}

// · #411 O3 p4 · fila del catálogo de productos (buscador «Desde catálogo»)
export interface ProductCatalogItem {
  id: string;
  name: string;
  manufacturer: string | null;
  serial_number: string | null;
  price_cents: number | null;
  currency: string;
  description: string | null;
  web_url: string | null;
  photo_urls: string[];
  category: string | null;
  source_selection_item_id: string | null;
  project_id: string | null;
  project_name?: string | null;
}

export interface SelectionItemCreate {
  project_id: string;
  title: string;
  category: SelectionCategory;
  space_id?: string;
  room_group?:
    | 'banos'
    | 'cine'
    | 'cocina'
    | 'dormitorios'
    | 'estudio'
    | 'exterior'
    | 'general'
    | 'paisajismo'
    | 'salon'
    | 'sotano'
    | 'zonas_paso';
  option_type?: 'principal' | 'alternativa';
  line_kind?: 'honorarios_internos' | 'producto' | 'honorarios_terceros' | 'suplido';
  // · #470 §7 · SOLO EN EL ALTA. El artículo nace sin economía y el API la ignora aunque se
  // mande. En el PATCH el API devuelve 409: cambiar la procedencia de una posición ya creada
  // borra su importe, y eso pasa por setSelectionProcedencia, que avisa antes.
  procedencia?: SelectionProcedencia;
  // · #399 p3 · null = "Sin proveedor" (el PATCH lo acepta como nullable)
  provider_id?: string | null;
  // · #406 p4 · null = vaciar el SKU desde el drawer (nullable en el API, como provider_url)
  provider_article_ref?: string | null;
  // · #399 p2 · null = vaciar el enlace web desde el drawer (nullable en el API)
  provider_url?: string | null;
  description?: string;
  image_url?: string;
  quantity?: number;
  cost_without_vat_cents?: number;
  provider_increment_pct?: number;
  provider_discount_pct?: number;
  studio_margin_pct?: number;
  order_status?: SelectionOrderStatus;
  plan_code?: string;
  plan_id?: string | null;
  observations?: string;
  internal_notes?: string;
  // Bloque A (0140) · opcionales nuevos
  parent_item_id?: string | null;
  availability?: SelectionAvailability;
  // · #399 p5/p8 · plazo de envío/entrega y medidas (null = vaciar)
  shipping_leadtime?: SelectionShippingLeadtime | null;
  measurements_text?: string | null;
  // · #411 p3 · unidad de medida (alta y edición); default 'ud' en el API
  unit?: SelectionUnit;
  review_status?: 'nuevo' | 'falta_info' | 'alerta' | 'revisado' | 'duda' | null;
  cross_reference_text?: string | null;
  position?: number | null;
  // #216/#221/#219 · campos económicos y logísticos editables
  dto_retention_pct?: number | null;
  // · #411 W4a · p5 · fija el DTO propio frente a la herencia del catálogo
  // · #450 · lo escribe quien tiene la economía del artículo (Diseño incluido)
  dto_override?: boolean;
  pricing_status?:
    | 'pendiente'
    | 'estimado'
    | 'revision'
    | 'final'
    | 'no_aplica'
    | 'gestionado_cliente'
    | 'gestionado_constructora'
    // · #399 extra · el enum del API ya lo tenía; sin él, los importados mostraban select fantasma
    | 'tarifa_catalogo';
  installation_required?: 'si' | 'si_proveedor' | 'no';
  logistics_mode?:
    | 'compra_directa'
    | 'recogida'
    | 'grua'
    | 'pie_calle_obra'
    | 'almacen_intermedio'
    | 'ubicacion_final';
  estimated_shipping_text?: string | null;
  estimated_reception_date?: string | null;
  deadline_at_destination?: string | null;
  minimum_destination_date?: string | null;
  scheduled_purchase_date?: string | null;
  // #263 · logística · nº de pedido y nº de factura (editables; el nº de suplido es read-only)
  purchase_order_ref?: string | null;
  provider_invoice_ref?: string | null;
}

// #191 · Flujo de revisión de selección por versiones
export type SelectionProcessState =
  | 'pre_revision'
  | 'en_ronda'
  | 'aprobada_compras'
  | 'en_ronda_cambios';
export type SelectionReviewType = 'revision' | 'final' | 'change';
// #441 · 'revoked' = ronda anulada por el equipo (terminal, igual que 'closed').
export type SelectionReviewCycleStatus = 'draft' | 'sent' | 'finishing' | 'closed' | 'revoked';

export interface SelectionReviewState {
  process_state: SelectionProcessState;
  active_review: {
    id: string;
    seq: number;
    type: SelectionReviewType;
    status: SelectionReviewCycleStatus;
    sent_at: string | null;
  } | null;
  change_pending_count: number;
}

export interface SelectionReviewHistoryRow {
  id: string;
  seq: number;
  type: SelectionReviewType;
  status: SelectionReviewCycleStatus;
  type_label: string;
  /** #441 · 'Cerrada' | 'Anulada' | 'Abierta' (lo calcula la vista, no el front). */
  status_label: string;
  sent_at: string | null;
  closed_at: string | null;
  closed_by_email: string | null;
  global_feedback: string | null;
  items_count: string;
  accepted_count: string;
  rejected_count: string;
  change_count: string;
  preferida_count: string;
  /** #449 · «la compro yo» (0253). Puede faltar si el API es viejo. */
  client_purchase_count?: string;
  conflict_count: string;
  // #441 · anulación (null si la ronda no está anulada)
  revoked_at: string | null;
  revoke_reason: string | null;
  revoked_by_name: string | null;
}

// #441 · detalle real que se enseña en la confirmación doble antes de anular una ronda.
export interface SelectionReviewRevokePreview {
  review_id: string;
  seq: number;
  type: SelectionReviewType;
  type_label: string;
  status: SelectionReviewCycleStatus;
  sent_at: string | null;
  items_count: number;
  decisions_count: number;
  decisions_by_kind: Record<string, number>;
  days_open: number | null;
  revocable: boolean;
  blocked_reason: string | null;
}

// Ítem de un snapshot/histórico (SIN economía interna · §6). total_client_cents = precio final.
export interface SelectionReviewSnapshotItem {
  id: string;
  item_identifier: string;
  option_type: 'principal' | 'alternativa';
  parent_identifier: string | null;
  title: string;
  description: string | null;
  image_url: string | null;
  category?: string | null;
  category_group_label: string | null;
  room_group?: string | null;
  space_label: string | null;
  provider_name: string | null;
  quantity: string;
  pricing_status?: string | null;
  order_status?: string | null;
  estimated_reception_date?: string | null;
  total_client_cents: string | null;
  position?: number | null;
  client_decision: string | null;
  client_note: string | null;
  /**
   * · #475 · cuándo decidió el cliente. La ruta ya lo devolvía (selection-review/index.ts) y el
   * tipo no lo declaraba, así que la fecha del comentario no se podía pintar. Opcional para
   * tolerar un API viejo.
   */
  client_decided_at?: string | null;
}

// Honorarios (M3.2)
export interface HonorariosCalcInput {
  parameter_set_id?: string;
  lead_id?: string;
  project_id?: string;
  model?: 'full' | 'consulta';
  // #170 p11 · plantilla de redacción del presupuesto
  template?: 'general' | 'amueblado';
  typology:
    | 'vivienda'
    | 'tienda'
    | 'restaurante'
    | 'hotel'
    | 'stand_feria'
    | 'consultoria_independiente';
  intervention:
    | 'decoracion_amueblado'
    | 'decoracion'
    | 'reforma'
    | 'reforma_integral'
    | 'obra_nueva'
    | 'rehabilitacion'
    | 'feria';
  area_interior_m2: number;
  area_exterior_m2?: number;
  rooms_count?: number | null;
  floors_count?: number | null;
  low_purchasing_zone?: boolean;
  pe_duration_months?: number;
  distance_km?: number;
  visits_count?: number;
  visit_present?: boolean;
  renders?: { type: 'estatico' | 'isometrico' | 'panoramico_360' | 'vr'; qty: number }[];
  task_overrides?: {
    code: string;
    required: 'si' | 'no' | 'fijo';
    workload: 'no_computa' | 'muy_baja' | 'baja' | 'media' | 'alta' | 'muy_alta';
  }[];
  // A4 · modo de cálculo (engine). 'automated' = importes del motor, redacción a medida
  calc_mode?: 'automated';
  // A4 · redacción a medida por línea (code → description) volcada en tasks_snapshot
  line_overrides?: { code: string; description: string }[];
  // #347 bloque E · redacción a medida por PARTIDA del documento (espejo del PDF, Paso 4/4)
  position_overrides?: { phase: string; task_type: string; description: string }[];
}

// #347 bloque E · partida del presupuesto tal y como la imprimirá el PDF (espejo exacto)
export interface HonorariosPresupuestoPosition {
  code: string; // [F.T] p.ej. "2.3"
  task_type: string;
  label: string;
  narrative: string | null; // texto canónico YA resuelto (variante + placeholders sustituidos)
  importe_cents: number;
  included_no_cost: boolean;
}

export interface HonorariosPresupuestoPhase {
  phase: string;
  phase_num: number;
  phase_label: string;
  positions: HonorariosPresupuestoPosition[];
  subtotal_cents: number;
}

export interface HonorariosTaskLine {
  code: string;
  phase: string;
  task_type: string;
  title: string;
  description?: string;
  required: 'si' | 'no' | 'fijo';
  workload: string;
  workload_weight: number;
  percentage: number;
  base_cost_cents: number;
  travel_extra_cents: number;
  total_cents: number;
  is_visit: boolean;
}

export interface HonorariosCalcResult {
  parameter_set_id: string;
  parameter_set_version: string;
  model: 'full' | 'consulta';
  typology: string;
  intervention: string;
  area_interior_m2: number;
  area_exterior_m2: number;
  area_weighted_m2: number;
  pe_duration_months: number;
  distance_km: number;
  visits_count: number;
  base_price_per_m2_cents: number;
  base_price_per_month_cents: number;
  max_cost_eiatpb_cents: number;
  max_cost_pe_cents: number;
  workload_eiatpb_sum: number;
  workload_pe_sum: number;
  multiplier_applied: number;
  tasks_snapshot: HonorariosTaskLine[];
  renders_breakdown: { type: string; qty: number; unit_price_cents: number; total_cents: number }[];
  renders_total_cents: number;
  travel_cost_cents: number;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  payment_plan: { phase: string; percentage: number; amount_cents: number; due_event: string }[];
  group_by_phase: { phase: string; subtotal_cents: number }[];
  // #347 bloque E · solo en el preview (/calculate): partidas espejo del PDF para el Paso 4/4
  positions?: HonorariosPresupuestoPhase[];
}

type ListResult<T> = { items: T[]; next_cursor: string | null };

// #350 · issue de validación Zod tal como lo devuelve la API en los 400 ({ status, validation })
export interface ApiValidationIssue {
  code?: string;
  message?: string;
  path?: (string | number)[];
  minimum?: number;
  maximum?: number;
  type?: string;
  expected?: string;
  received?: string;
}

// #350 · traduce UN issue de Zod a texto corto en español (fallback: message original)
export function apiIssueText(i: ApiValidationIssue): string {
  if (i.code === 'too_small') {
    if (i.type === 'string') return `mínimo ${i.minimum} caracteres`;
    if (i.type === 'array') return `mínimo ${i.minimum} elemento(s)`;
    return `debe ser ≥ ${i.minimum}`;
  }
  if (i.code === 'too_big') {
    if (i.type === 'string') return `máximo ${i.maximum} caracteres`;
    if (i.type === 'array') return `máximo ${i.maximum} elemento(s)`;
    return `debe ser ≤ ${i.maximum}`;
  }
  if (i.code === 'invalid_type') {
    return i.received === 'undefined' ? 'campo obligatorio' : 'tipo no válido';
  }
  if (i.code === 'invalid_enum_value' || i.code === 'invalid_string') return 'valor no válido';
  return i.message || 'valor no válido';
}

// #350 · ['breakdown', 0, 'notes'] → 'breakdown[1].notes' (índices 1-based, legible)
function apiIssuePath(path: (string | number)[] | undefined): string {
  if (!path || path.length === 0) return '';
  return path
    .map((p, idx) => (typeof p === 'number' ? `[${p + 1}]` : idx === 0 ? String(p) : `.${p}`))
    .join('');
}

export function formatValidationIssues(issues: ApiValidationIssue[]): string {
  return issues
    .map((i) => {
      const p = apiIssuePath(i.path);
      return p ? `${p}: ${apiIssueText(i)}` : apiIssueText(i);
    })
    .join(' · ');
}

// #405 · Error de API que arrastra el código de estado, para que la UI pueda distinguir un
// 429 transitorio (rate limit) de un error de verdad sin parsear el texto del mensaje.
export interface ApiError extends Error {
  status?: number;
  // #412 · código de negocio del backend (p.ej. NOT_CURRENT_CALCULATION) para ramificar en la UI
  code?: string;
}
function apiError(status: number, message: string, code?: string): ApiError {
  return Object.assign(new Error(message), code ? { status, code } : { status });
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const doFetch = (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...init?.headers },
    });

  let res = await doFetch();

  // #405 · un 429 (rate limit) en un GET idempotente se reintenta UNA vez con un pequeño
  // backoff (respetando Retry-After si viene, con tope de 5 s) antes de propagar el error.
  // Los métodos con efectos (POST/PATCH/…) NO se reintentan solos.
  const method = (init?.method ?? 'GET').toUpperCase();
  if (res.status === 429 && method === 'GET') {
    const retryAfter = Number(res.headers.get('Retry-After'));
    const waitMs =
      Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter * 1000, 5000) : 400;
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    res = await doFetch();
  }

  if (!res.ok) {
    // #146 · sesión caducada: mensaje accionable en lugar de "Unauthorized".
    // El formulario conserva lo escrito: renovar sesión en otra pestaña y re-guardar.
    if (res.status === 401) {
      throw apiError(
        401,
        'Tu sesión ha caducado. Abre os.munya.studio en otra pestaña para volver a entrar y pulsa Guardar de nuevo — lo escrito no se pierde.',
      );
    }
    const body = (await res.json().catch(() => ({}))) as {
      detail?: string;
      title?: string;
      // #405 · @fastify/rate-limit responde con { error, message } (no detail/title).
      message?: string;
      validation?: ApiValidationIssue[];
      // #412 · código de negocio para ramificar en la UI (p.ej. confirmar y reintentar)
      code?: string;
    };
    // #350 · los 400 de validación Zod llegan sin title/detail: formatear los issues legibles
    // en vez del "HTTP 400" pelado.
    if (Array.isArray(body.validation) && body.validation.length > 0) {
      const base = body.title || 'Datos no válidos';
      throw apiError(res.status, `${base} · ${formatValidationIssues(body.validation)}`);
    }
    // #337 · detail/title pueden llegar como string VACÍO (p.ej. GraphError sin message):
    // con `??` ese vacío se propagaba y la UI pintaba cajas de aviso sin texto → `||`.
    // #405 · `message` cubre el 429 del rate limit de @fastify (mensaje ya en castellano).
    throw apiError(
      res.status,
      body.detail || body.message || body.title || `HTTP ${res.status}`,
      body.code,
    );
  }
  return (await res.json()) as T;
}

// A9 V1.5 · campos editables del parameter_set (sin exponer fórmulas internas del motor)
export interface HonorariosParameterSetEditable {
  description: string;
  multiplier_vivienda: number;
  multiplier_tienda: number;
  multiplier_restaurante: number;
  multiplier_hotel: number;
  multiplier_stand_feria: number;
  multiplier_decoracion: number;
  multiplier_reforma: number;
  multiplier_reforma_integral: number;
  multiplier_obra_nueva: number;
  multiplier_rehabilitacion: number;
  multiplier_feria: number;
  surcharge_2_plus_floors: number;
  surcharge_3_plus_rooms: number;
  discount_low_purchasing: number;
  exterior_weight: number;
  price_per_m2_at_40: number;
  price_per_m2_at_600: number;
  price_per_month_at_1: number;
  price_per_month_at_4: number;
  pe_included_m2: number;
  pe_doc_eur_per_m2: number;
  pe_distance_surcharge: number;
  pe_distance_threshold_km: number;
  price_render_estatico: number;
  price_render_isometrico: number;
  price_render_360: number;
  price_render_vr: number;
  consulta_base_cents: number;
  consulta_included_m2: number;
  consulta_block_m2: number;
  consulta_block_cents: number;
  consulta_visit_cents: number;
  consulta_travel_tier1_max_km: number;
  consulta_travel_tier2_max_km: number;
  consulta_travel_tier2_cents: number;
  payment_split_p1_pct: number;
  payment_split_p2_pct: number;
  payment_split_p3_pct: number;
  payment_split_p4_pct: number;
  tax_rate: number;
  travel_cost_per_km: number;
  travel_round_trip: boolean;
}

export interface HonorariosParameterSetFull extends HonorariosParameterSetEditable {
  id: string;
  version: string;
  is_active: boolean;
  superseded_by: string | null;
  created_at: string;
}

export interface OneDriveSyncCandidate {
  path: string;
  name: string;
  size: number;
  suggested: boolean;
  flags: string[];
}
export interface OneDriveSyncProject {
  project_id: string;
  project_name: string | null;
  project_exists: boolean;
  folder: string;
  seleccion: OneDriveSyncCandidate[];
  track: OneDriveSyncCandidate[];
  notes: string[];
}

// #369 · import de OneDrive asíncrono (job + polling)
export type OneDriveImportKind = 'selection' | 'track';
// Respuesta 202 del POST de import: el job ya está corriendo en 2º plano.
export interface OneDriveImportAck {
  job_id: string;
  kind: OneDriveImportKind;
  existing: boolean; // true = ya había un job running de ese proyecto+kind, se reutilizó
}
// Resumen que devuelve un job de selección/track al terminar (result del job done).
export interface OneDriveSelectionResult {
  source: { name: string; size: number };
  replaced: number;
  sheets_processed?: string[];
  items_created: number;
  alternatives_linked?: number;
  // · #473 p4 · alternativas colgadas por respaldo (Secuencia+Ubicación o adyacencia) y las que
  // se han quedado SUELTAS (se pintan como principales: es lo que dejó Kaia con 11 falsos).
  alternatives_by_fallback?: number;
  alternatives_orphan?: number;
  spaces_created?: number;
  providers_created?: number;
  // · #473 p8 · incidencias graves, para pintarlas EN PANTALLA y no solo en el informe .md
  critical?: string[];
  warnings?: string[];
  // · #439 · toda ejecución (real o ensayo) deja un informe descargable y queda registrada.
  run_id?: string;
  report_url?: string;
  mode?: 'import' | 'ensayo';
}

// · #439 §4.2 · precondiciones de una importación de selección. Se consultan ANTES de lanzar
// nada para poder decir «esto no se puede, y esto es lo que falta» en vez de romper datos.
export interface ImportPrecondition {
  code:
    | 'version'
    | 'plantilla'
    | 'ubicaciones'
    | 'ronda_abierta'
    | 'duplicado'
    | 'lineas_pedido'
    | 'respaldo'
    // · #473 · el fichero es el de ESTE proyecto · y no hay dos maestros bajo 05_INTERIORISMO
    | 'fichero_del_proyecto'
    | 'maestro_duplicado';
  ok: boolean;
  blocking: boolean;
  title: string;
  detail: string;
  fix?: string;
}
export interface ImportPreconditionResult {
  ok: boolean;
  checks: ImportPrecondition[];
  blocking: ImportPrecondition[];
  summary: string;
  master_version: number | null;
}

// · #439 · copias de seguridad de la selección, con su estado. `available: false` = la tabla
// ya no está (purgada a los 30 días o borrada a mano): sin botón de restaurar.
export interface SelectionImportBackup {
  id: string;
  table_name: string;
  archived_table: string | null;
  items: number;
  media_rows: number;
  links_rows: number;
  created_at: string;
  archived_at: string | null;
  restored_at: string | null;
  purged_at: string | null;
  available: boolean;
}
export interface OneDriveTrackResult {
  source: { name: string; size: number };
  internal_header?: string;
  items_parsed: number;
  items_created: number;
  warnings?: string[];
}
export interface OneDriveImportJob {
  id: string;
  project_id: string;
  kind: OneDriveImportKind;
  status: 'running' | 'done' | 'error';
  result: OneDriveSelectionResult | OneDriveTrackResult | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

// ── Hub del lead (H0) ─────────────────────────────────────────────────────
// 4º uso del enlace mágico (rol reducido). El hub REFLEJA la ficha; nunca escribe
// estado de negocio. Espejo de los tipos de apps/api/src/services/hub/hub-core.ts.
export type HubModuleKey =
  | 'vsl'
  | 'cuaderno'
  | 'booking'
  | 'cita'
  | 'cuestionario'
  | 'docu_upload'
  | 'propuesta'
  | 'firma'
  | 'transicion';
export type HubModules = Record<HubModuleKey, boolean>;
export type HubLinkStatus = 'active' | 'expired' | 'revoked';
export type HubVia = 'verde' | 'revisar' | 'ambar';

export interface LeadHubEvent {
  type: string;
  // timestamptz → Date serializado como string ISO en el JSON de la API
  occurred_at: string;
  meta: unknown;
}
export interface LeadHubState {
  slug: string;
  url: string;
  locale: string;
  via_inicial: HubVia | null;
  status: HubLinkStatus;
  days_remaining: number;
  created_at: string;
  expires_at: string;
  renewed_at: string | null;
  revoked_at: string | null;
  modules: HubModules;
}
// GET /leads/:id/hub · hub === null cuando el lead aún no tiene hub emitido.
export interface LeadHubResponse {
  hub: LeadHubState | null;
  activity?: LeadHubEvent[];
}

// GET /hub/:slug (público, sin auth). Estados: activo (200 status=active), TRÁNSITO
// (200 status=transition · convertido, anuncia el portal), fin de vida (410), 404.
export interface HubPublicData {
  slug: string;
  locale: string;
  first_name: string | null;
  project: string | null;
  modules: HubModuleKey[];
  moment: string;
  // Assets del estudio para M0 (null si el módulo está off o sin URL configurada).
  vsl_url: string | null;
  // VSL responsive (#371 v2): horizontal (ordenador) / vertical (móvil). Cada uno cae a
  // vsl_url si no está configurado; null si el módulo 'vsl' está off.
  vsl_url_horizontal: string | null;
  vsl_url_vertical: string | null;
  cuaderno_url: string | null;
  // Carátula del cuaderno (#371 v2 corrección 14-jul): null si no hay thumbnail (el front
  // cae a solo texto+botón) o si cuaderno_url es una URL fija de studio_branding.
  cuaderno_cover_url: string | null;
  // Flags de estado (#371 v2): la web pliega/oculta según estos (check + resumen al
  // completar). El vídeo de bienvenida YA NO tiene flag propio: su visibilidad ES el
  // módulo 'vsl' de `modules` (apagado al agendar, reactivable desde la ficha).
  booking_confirmed: boolean;
  // #448 · el lead pidió otra franja («ninguna hora me encaja») y todavía no tiene cita. Es el
  // ÚNICO caso en que se le dice «estamos ultimando los detalles»: sin esto, ese mensaje salía
  // por defecto a quien sí tenía videollamada y a quien no tenía ninguna.
  booking_awaiting_reschedule?: boolean;
  questionnaire_completed: boolean;
  steps: {
    welcome: 'open' | 'done';
    booking: 'open' | 'done';
    questionnaire: 'open' | 'done';
  };
  // Duración de la videollamada (min) · fuente única hub_booking_config.slot_minutes.
  videocall_minutes: number;
  status: 'active';
}
// M4 · TRÁNSITO (convertido): el token está revocado pero la página sigue viva para
// pasar al lead de un enlace (el hub) al siguiente (el portal de cliente). Sin cifras.
export interface HubPublicTransition {
  slug: string;
  locale: string;
  first_name: string | null;
  project: string | null;
  modules: HubModuleKey[]; // ['transicion']
  moment: 'cerrado';
  // Enlace de ENTRADA al portal (no el magic-link, que ya viajó por email en la cascada).
  portal_url: string | null;
  status: 'transition';
}
export type HubPublicResult =
  | { kind: 'ok'; data: HubPublicData }
  | { kind: 'transition'; data: HubPublicTransition }
  | { kind: 'end_of_life'; message: string }
  | { kind: 'not_found' };

// GET /hub/privacy?locale=es|en|de (público). Texto de privacidad del hub (RGPD §12.13).
// Espejo de apps/api/src/services/hub/hub-privacy.ts.
export interface HubPrivacyText {
  locale: 'es' | 'en' | 'de';
  title: string;
  paragraphs: string[];
  contact_email: string;
  updated_at: string;
}

// ── Módulos ricos de M0 (acciones públicas del hub, sin auth) ─────────────────
// · #395 · Cuestionario = BRIEFING CANÓNICO del estudio (paridad con munya.studio):
// 12 capítulos, ~130 preguntas, cuatro ramas por uso final y taxonomías idénticas a las
// del formulario de la web. · #396 · capítulo final «El proyecto y su porqué».
//
// El front ya NO conoce el esquema: la API sirve los METADATOS (capítulos, campos,
// etiquetas, opciones y lógica condicional) y aquí solo se renderiza. Por eso las
// respuestas son un diccionario genérico en vez de una interfaz espejo — así el esquema
// del briefing puede crecer sin arrastrar al cliente. La validación real (enums, límites)
// vive en el zod de apps/api/src/services/hub/questionnaire.ts.
export type HubAnswerValue = string | number | boolean | string[];
export type HubQuestionnaireAnswers = Record<string, HubAnswerValue | undefined>;

export type HubStepFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'radio' // una sola opción, presentada como chips/tarjetas (escala de agrado, pares…)
  | 'multiselect'
  | 'ranking' // ordenación sobre una lista fija de opciones (por persona)
  | 'note'; // texto informativo sin respuesta (nota de honorarios, enunciados)
// Condición de la lógica condicional. En multiselección se cumple si el array CONTIENE
// alguno de los valores (así el chip «Otra…» abre su campo de texto libre).
export interface HubFieldCondition {
  field: string;
  in: string[];
}
// Metadatos de un campo del briefing (los sirve la API; el front los renderiza).
export interface HubStepFieldMeta {
  key: string;
  label: string;
  type: HubStepFieldType;
  prefilled?: boolean;
  options?: { value: string; label: string }[];
  image?: string; // imagen de referencia (los estilos)
  visible_if?: HubFieldCondition; // se muestra solo si se cumple
  hidden_if?: HubFieldCondition; // se OCULTA si se cumple (condición negativa)
}
export interface HubStepMeta {
  id: number;
  key: string;
  title: string;
  intro?: string;
  visible_if?: HubFieldCondition; // rama: el capítulo entero depende del uso final
  fields: HubStepFieldMeta[];
}
export interface HubQuestionnaireState {
  answers: HubQuestionnaireAnswers;
  progress: number; // 0-100
  completed: boolean;
  // Estructura de los capítulos (fuente única en la API).
  steps: HubStepMeta[];
}
export interface HubUploadedFile {
  name: string;
  uploaded_at: string | null;
  size: number | null;
}
export interface HubUploadsResult {
  files: HubUploadedFile[];
  available: boolean;
}
export interface HubUploadResult {
  ok: boolean;
  stored: 'onedrive' | 'skipped';
  filename: string;
  size: number;
}
// Subida grande (#371 v2): la API crea la Graph upload session y devuelve la uploadUrl
// pre-autenticada; el navegador sube directo a Graph por chunks y avisa al terminar.
export interface HubUploadSession {
  upload_url: string;
  expires_at: string;
  chunk_bytes: number;
  max_bytes: number;
}
export type HubEventType = 'vsl_play' | 'vsl_progress' | 'cuaderno_download';

// ── M1/M2 · Reserva de videollamada (H2 · acciones públicas) ──────────────────
// Espejo de apps/api/src/routes/leads/hub-booking.ts. Instantes (starts_at/ends_at)
// llegan como string ISO (postgres.js/Date serializado). Las etiquetas vienen ya
// formateadas por la API en la zona del estudio y el locale del lead.
export interface HubBookingSlot {
  starts_at: string;
  ends_at: string;
  label: string; // hora local del estudio "HH:MM"
}
export interface HubBookingDay {
  date: string; // YYYY-MM-DD en la zona del estudio
  slots: HubBookingSlot[];
}
export interface HubBookingAvailability {
  timezone: string;
  slot_minutes: number;
  days: HubBookingDay[];
}
export interface HubBookingConfirmed {
  starts_at: string;
  ends_at: string;
  label: string; // fecha + hora larga
  time_label?: string; // solo hora (presente en el GET)
  timezone: string;
  teams_url: string | null;
  attendees_note: string | null;
  // #448 · la cita del hub es ahora la VIGENTE de lead_meetings, venga de la reserva del propio
  // lead o de la ficha del CRM. Por eso llegan también asunto, hora de fin, duración y el .ics.
  subject?: string;
  end_time_label?: string;
  minutes?: number;
  /** Ruta del fichero .ics de «Añadir a mi calendario». */
  ics_url?: string;
  // #454 · estado del enlace «Cambiar la hora». Lo decide el SERVIDOR (mismas reglas que
  // aplica el POST de reprogramación): la pantalla solo obedece.
  reschedule?: HubRescheduleState;
}

/** #454 · por qué el lead no puede mover su cita (el front lo traduce a es/en/de). */
export type HubRescheduleReason =
  | 'not_live'
  | 'too_late'
  | 'limit_reached'
  | 'no_outlook_event'
  | 'slot_taken'
  | 'graph_down';

export interface HubRescheduleState {
  allowed: boolean;
  reason: HubRescheduleReason | null;
  /** Veces que ya la ha movido el lead. */
  used: number;
  /** Cupo (2). */
  limit: number;
}

// #392 · Config del motor de reservas (Administración › Citas del hub).
// Espejo de hub_booking_config (mig 0213) + los endpoints admin de
// apps/api/src/routes/leads/hub-booking.ts. Los tramos son pares ["HH:MM","HH:MM"]
// en la zona horaria del estudio; un día sin tramos es un día sin videollamadas.
export type HubBookingWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type HubBookingTimeRange = [string, string];
export type HubBookingWeeklyAvailability = Record<HubBookingWeekday, HubBookingTimeRange[]>;

export interface HubBookingConfig {
  weekly_availability: HubBookingWeeklyAvailability;
  slot_minutes: number;
  min_advance_hours: number;
  buffer_minutes: number;
  max_per_day: number;
  /** null → el motor cae al buzón de notificaciones del entorno. */
  organizer_mailbox: string | null;
  timezone: string;
}

export interface HubBookingConfigResponse {
  config: HubBookingConfig;
  /** Buzón realmente usado (config o el del entorno) — solo informativo. */
  effective_organizer_mailbox: string | null;
}

// ── Propuesta a leads (#360 · M3, ruta e identidad propias) ───────────────────
// Cara PÚBLICA (os.munya.studio/propuesta/{slug}, sin login, gate de clave) + cara
// INTERNA (drawer del lead, Dirección). Espejo de apps/api/src/services/proposals/
// proposal-core.ts + routes/leads/proposals(-public).ts. numeric/bigint → number ya
// resueltos por la API (costeCents/totalCents son enteros). valid_until → ISO string.
// · #393 · idioma del documento: un solo idioma por propuesta, el de la ficha del cliente.
export type ProposalLocale = 'es' | 'en' | 'de';

// · #393 · el «cuarteto» de cada fase sale del catálogo por fase e idioma
// (Administración → Honorarios → «Textos del presupuesto»), NO de las líneas del
// presupuesto: ese desglose partida a partida vive solo en el PDF.
export interface ProposalPhaseLine {
  key: string;
  label: string;
  objetivo: string;
  contempla: string;
  entrega: string;
  intervencion: string;
  // Coste de la fase (cents, sin IVA). Presente SOLO tras la clave (includesFigures).
  costeCents?: number;
}
// Hito del plan de pagos del presupuesto vinculado (importes CON IVA, como el plan).
export interface ProposalMilestone {
  dueEvent: string;
  amountCents: number;
}
export interface ProposalInvestment {
  phases: ProposalPhaseLine[];
  // · #393 · el TOTAL es el protagonista de la sección: ya no hay framing "desde X €".
  subtotalCents?: number; // total sin IVA
  totalCents?: number; // total con IVA
  taxRatePct?: number;
  // Imágenes 3D contratadas: línea propia porque no están en las fases (si no, no suma).
  rendersCents?: number;
  milestones: ProposalMilestone[];
}
// Tarjeta de persona en «El estudio».
// · #465 · todo viene de la ficha de la persona (Administración → Equipo): retrato, nombre
// público y cargo/área YA en el idioma del documento. El cargo y el área ya no viajan
// compilados en este bundle, así que un cambio de equipo no exige un despliegue.
export interface ProposalTeamMember {
  name: string;
  initials: string;
  photoUrl: string | null;
  role: string;
  area: string;
}
// Numeral editorial del punto de partida (dato de la ficha del lead).
export interface ProposalDato {
  key: 'superficie' | 'tipologia';
  value: string;
}
export interface ProposalSections {
  includesFigures: boolean;
  locale: ProposalLocale;
  hero: {
    projectName: string | null;
    titular: string | null;
    ubicacion: string | null;
    // Nunca vacío: siempre hay imagen en el hero (#393).
    imageUrl: string;
  };
  estudio: { team: ProposalTeamMember[] };
  proyecto: {
    projectName: string | null;
    // Primera línea de la promesa (la cita con filete olivo) + resto en párrafos.
    cita: string | null;
    narrativa: string[];
    // 0 a 4 claves. Con 0 el bloque no se renderiza y no deja hueco.
    claves: string[];
  };
  punto_de_partida: { texto: string | null; datos: ProposalDato[] };
  recorrido_inversion: ProposalInvestment;
  cierre: { texto: string | null; imageUrl: string };
}
// GET /propuesta/:slug SIN token válido: solo lo no sensible (portada + gate). El idioma
// viaja ya aquí: la puerta de clave se pinta en el idioma del lead (#393).
export interface ProposalLockedView {
  slug: string;
  status: string;
  requires_key: boolean;
  expired: boolean;
  locked: boolean;
  locale: ProposalLocale;
  portada: { titular: string | null; project_name: string | null };
}
// GET /propuesta/:slug CON token válido: el documento completo con cifras.
export interface ProposalUnlockedView {
  slug: string;
  status: string;
  requires_key: boolean;
  expired: boolean;
  locked: false;
  locale: ProposalLocale;
  project_name: string | null;
  valid_until: string | null;
  sections: ProposalSections;
}
export type ProposalGetResult =
  | { kind: 'locked'; data: ProposalLockedView }
  | { kind: 'unlocked'; data: ProposalUnlockedView }
  | { kind: 'not_found' };
export type ProposalUnlockResult =
  | { kind: 'ok'; token: string }
  | { kind: 'wrong_key' }
  | { kind: 'rate_limited'; retry_after: number }
  | { kind: 'error'; message: string };
export type ProposalAcceptResult =
  | {
      kind: 'ok';
      signature_state: 'requested' | 'signed';
      submission_id: string | number;
      already_launched: boolean;
    }
  | { kind: 'error'; status: number; message: string };

// Cara INTERNA (drawer): estado de la propuesta activa del lead.
export interface LeadProposalState {
  id: string;
  lead_id: string;
  slug: string;
  url: string;
  status: 'draft' | 'sent' | 'accepted' | 'expired' | 'revoked';
  has_access_key: boolean;
  proposal_calculation_id: string | null;
  valid_until: string | null;
  project_name: string | null;
  copy: {
    titular?: string;
    promesa?: string;
    /** LEGADO #360 · la página pública ya no lo pinta («El estudio» es plantilla · #393). */
    enfoque?: string;
    punto_de_partida?: string;
    cierre?: string;
    /** · #393 · claves del proyecto: 0 a 4 líneas cortas. */
    claves?: string[];
  };
  /** · #393 · selector de imagen del hero: clave del banco de serie o 'custom'. */
  hero_image_key: string;
  hero_image_url: string | null;
  created_at: string;
  sent_at: string | null;
  accepted_at: string | null;
}
export type LeadProposalCopy = LeadProposalState['copy'];

// · #399 p6 · respuesta del alta de imagen server-side (from-almacen / upload)
export interface SelectionMediaCreated {
  id: string;
  original_url: string;
  pipeline: { ok: boolean; error?: string; variants_generated?: number };
}

// · #457 p3 · resultado del recálculo de la principal, que el servidor devuelve en toda
// operación de media (alta, reproceso, borrado y —#483— elección de portada y reordenado).
export interface SelectionMediaPrimary {
  primary_media_id: string | null;
  image_url: string | null;
  promoted: boolean;
}

export const api = {
  // Hub público del lead · fetch propio (no `req`): hay que distinguir 200 / 410
  // (fin de vida, página cordial) / 404 sin que un throw genérico los aplane.
  hubPublic: async (slug: string): Promise<HubPublicResult> => {
    const res = await fetch(`${BASE}/hub/${encodeURIComponent(slug)}`, {
      headers: { 'content-type': 'application/json' },
    });
    if (res.ok) {
      // Un 200 puede ser hub ACTIVO o TRÁNSITO (convertido): los separa `status`.
      const data = (await res.json().catch(() => null)) as
        | (HubPublicData | HubPublicTransition)
        | null;
      if (data && data.status === 'transition') {
        return { kind: 'transition', data };
      }
      return { kind: 'ok', data: data! };
    }
    if (res.status === 410) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      return { kind: 'end_of_life', message: body.message ?? 'Este enlace ya no está activo.' };
    }
    return { kind: 'not_found' };
  },
  // Texto de privacidad del hub (RGPD §12.13). Público, siempre 200 (fallback a 'es').
  hubPrivacy: (locale?: string) =>
    req<HubPrivacyText>(`/hub/privacy${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`),
  // Drawer interno del lead (Dirección): estado del enlace + módulos + actividad.
  leadHub: (id: string) => req<LeadHubResponse>(`/leads/${id}/hub`),
  // Emitir/reenviar (phone/referral o reenvío manual). Body opcional (vía + resend).
  leadHubEmit: (id: string, body?: { via_inicial?: HubVia; resend?: boolean }) =>
    req<{
      hub_id: string;
      slug: string;
      url: string;
      email_queued: boolean;
      email_skipped_reason: string | null;
    }>(`/leads/${id}/hub/emit`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  leadHubRenew: (id: string) =>
    req<{ ok: boolean; expires_at: string }>(`/leads/${id}/hub/renew`, { method: 'POST' }),
  leadHubRevoke: (id: string) =>
    req<{ ok: boolean }>(`/leads/${id}/hub/revoke`, { method: 'POST' }),
  leadHubRegenerate: (id: string) =>
    req<{ ok: boolean; slug: string; url: string }>(`/leads/${id}/hub/regenerate`, {
      method: 'POST',
    }),
  leadHubSetModule: (id: string, key: HubModuleKey, on: boolean) =>
    req<{ ok: boolean; modules: HubModules }>(`/leads/${id}/hub/modules`, {
      method: 'POST',
      body: JSON.stringify({ key, on }),
    }),
  // ── Acciones públicas del hub (sin auth, gateadas por módulo en la API) ──────
  // Sube documentación previa (planos/fotos/vídeos). content_base64 SIN el prefijo
  // data:...;base64,. La API degrada a 202 sin Graph; los errores (413/415/400) llegan
  // como Error con mensaje legible vía `req`.
  hubUpload: (
    slug: string,
    body: { filename: string; content_base64: string; content_type?: string },
  ) =>
    req<HubUploadResult>(`/hub/${encodeURIComponent(slug)}/upload`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Subida GRANDE (>4 MB): la API crea la Graph upload session (503 sin Graph/carpeta,
  // 415 ext, 413 >100MB). El navegador sube directo a Graph por chunks (subida-grande) y
  // luego llama a hubUploadRegistered para el evento + aviso interno.
  hubUploadSession: (
    slug: string,
    body: { filename: string; size: number; content_type?: string },
  ) =>
    req<HubUploadSession>(`/hub/${encodeURIComponent(slug)}/upload/session`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  hubUploadRegistered: (slug: string, body: { filename: string; size: number }) =>
    req<{ ok: boolean }>(`/hub/${encodeURIComponent(slug)}/upload/registered`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #460 · Un intento que NO llegó. Deja hub_event 'file_upload_failed' (nombre, peso,
  // motivo) y avisa al estudio: un lead que no consigue subir es una señal comercial, y
  // hasta ahora no dejaba rastro ninguno. Best-effort desde el front (si esto también
  // falla, no hay nada más que hacer desde el navegador).
  hubUploadFailed: (
    slug: string,
    body: {
      filename: string;
      size: number;
      stage: 'inline' | 'session' | 'directo' | 'registro';
      reason?: string;
    },
  ) =>
    req<{ ok: boolean }>(`/hub/${encodeURIComponent(slug)}/upload/failed`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  hubUploads: (slug: string) => req<HubUploadsResult>(`/hub/${encodeURIComponent(slug)}/uploads`),
  hubQuestionnaireGet: (slug: string) =>
    req<HubQuestionnaireState>(`/hub/${encodeURIComponent(slug)}/questionnaire`),
  // Guardado retomable (parcial) o completado (completed:true → vuelca briefing una vez).
  hubQuestionnairePut: (
    slug: string,
    body: { answers: HubQuestionnaireAnswers; progress?: number; completed?: boolean },
  ) =>
    req<HubQuestionnaireState & { ok: boolean }>(`/hub/${encodeURIComponent(slug)}/questionnaire`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  hubEvent: (slug: string, body: { type: HubEventType; meta?: Record<string, unknown> }) =>
    req<{ ok: boolean }>(`/hub/${encodeURIComponent(slug)}/event`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // ── M1 · Reserva (module 'booking') ──────────────────────────────────────────
  // Huecos libres futuros por día (zona del estudio). El motor recomputa disponibilidad.
  // `forReschedule` (#454 §4.2.5): el panel de «Cambiar la hora» pide los huecos SIN contar la
  // cita que el propio lead está moviendo — ni su hueco, ni el cupo de citas de ese día. Sin el
  // flag, un lead cuya cita cae en un día ya lleno no vería un solo hueco ese día.
  hubBookingAvailability: (slug: string, forReschedule = false) =>
    req<HubBookingAvailability>(
      `/hub/${encodeURIComponent(slug)}/booking/availability${forReschedule ? '?reschedule=1' : ''}`,
    ),
  // Reservar un hueco. 409 ("slot ya no disponible") si otro lead lo cogió → recargar.
  hubBookingCreate: (slug: string, body: { starts_at: string; attendees_note?: string }) =>
    req<{ ok: boolean; booking: HubBookingConfirmed }>(`/hub/${encodeURIComponent(slug)}/booking`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // "Ninguna de estas horas me encaja": actividad + aviso a Dirección. No crea cita.
  hubBookingNoneFit: (slug: string, body?: { note?: string }) =>
    req<{ ok: boolean }>(`/hub/${encodeURIComponent(slug)}/booking/none-fit`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  // ── M2 · Cita confirmada (module 'cita') ────────────────────────────────────
  // `awaiting_reschedule` distingue las DOS razones de no tener cita: que el lead haya pedido
  // otro hueco («ninguna hora me encaja» → «estamos ultimando los detalles») o que sencillamente
  // no haya ninguna en pie, en cuyo caso vuelve el calendario de reserva (#448 §7).
  hubBookingGet: (slug: string) =>
    req<{
      booking: HubBookingConfirmed | null;
      slot_minutes: number;
      awaiting_reschedule?: boolean;
    }>(`/hub/${encodeURIComponent(slug)}/booking`),
  // #454 · El lead mueve SU cita. Reprograma el MISMO evento de Outlook (conserva el enlace de
  // Teams) y no crea cita nueva. Las reglas las valida el servidor: 409 con `reason` legible
  // ('too_late' | 'limit_reached' | 'not_live' | 'slot_taken') sin perder la cita actual.
  hubBookingReschedule: (slug: string, body: { starts_at: string }) =>
    req<{
      ok: boolean;
      moved: boolean;
      previous?: { starts_at: string; label: string };
      booking: HubBookingConfirmed;
    }>(`/hub/${encodeURIComponent(slug)}/booking/reschedule`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // "¿Quién se conectará?": cae en la ficha como nota de preparación (decisor secundario).
  hubBookingAttendees: (slug: string, body: { attendees_note: string }) =>
    req<{ ok: boolean; attendees_note: string }>(
      `/hub/${encodeURIComponent(slug)}/booking/attendees`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  // ── #392 · Config del motor (Administración › Citas del hub · solo Dirección) ─
  // Los cambios solo afectan a los huecos que se calculen DESPUÉS: las reservas ya
  // hechas no se recalculan. Un 400 llega con los mensajes de validación en español.
  hubBookingConfigGet: () => req<HubBookingConfigResponse>('/admin/hub-booking-config'),
  hubBookingConfigSave: (body: HubBookingConfig) =>
    req<HubBookingConfigResponse & { ok: boolean }>('/admin/hub-booking-config', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  // ── Propuesta PÚBLICA (#360 · sin login, gate de clave) ──────────────────────
  // fetch propio (no `req`): un 401 aquí es "clave requerida/incorrecta", NO sesión
  // caducada. El token de sesión (tras /unlock) se pasa por cabecera x-proposal-token;
  // `fromHub` añade ?from=hub para que la analítica del hub cuente la visita cuando la
  // propuesta se abre desde el enlace del hub (tráfico directo no infla el hub).
  proposalGet: async (
    slug: string,
    token?: string | null,
    fromHub?: boolean,
  ): Promise<ProposalGetResult> => {
    const qs = fromHub ? '?from=hub' : '';
    const res = await fetch(`${BASE}/propuesta/${encodeURIComponent(slug)}${qs}`, {
      headers: token ? { 'x-proposal-token': token } : {},
    });
    if (res.status === 404) return { kind: 'not_found' };
    const body = (await res.json().catch(() => null)) as unknown;
    if (!body || typeof body !== 'object') return { kind: 'not_found' };
    if ('sections' in body) return { kind: 'unlocked', data: body as ProposalUnlockedView };
    return { kind: 'locked', data: body as ProposalLockedView };
  },
  proposalUnlock: async (slug: string, key: string): Promise<ProposalUnlockResult> => {
    const res = await fetch(`${BASE}/propuesta/${encodeURIComponent(slug)}/unlock`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ key }),
    });
    if (res.ok) {
      const b = (await res.json().catch(() => ({}))) as { token?: string };
      return b.token ? { kind: 'ok', token: b.token } : { kind: 'error', message: 'Sin token' };
    }
    if (res.status === 429) {
      const ra = Number(res.headers.get('Retry-After') ?? '60');
      return { kind: 'rate_limited', retry_after: Number.isFinite(ra) ? ra : 60 };
    }
    if (res.status === 401) return { kind: 'wrong_key' };
    const b = (await res.json().catch(() => ({}))) as { detail?: string; title?: string };
    return { kind: 'error', message: b.detail || b.title || 'No se pudo validar la clave.' };
  },
  proposalAccept: async (
    slug: string,
    token: string | null,
    fromHub?: boolean,
  ): Promise<ProposalAcceptResult> => {
    const qs = fromHub ? '?from=hub' : '';
    const res = await fetch(`${BASE}/propuesta/${encodeURIComponent(slug)}/accept${qs}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { 'x-proposal-token': token } : {}),
      },
    });
    const body = (await res.json().catch(() => ({}))) as {
      signature_state?: 'requested' | 'signed';
      submission_id?: string | number;
      already_launched?: boolean;
      detail?: string;
      title?: string;
    };
    if (res.ok) {
      return {
        kind: 'ok',
        signature_state: body.signature_state ?? 'requested',
        submission_id: body.submission_id ?? '',
        already_launched: Boolean(body.already_launched),
      };
    }
    return { kind: 'error', status: res.status, message: body.detail || body.title || 'Error' };
  },
  // Descarga el presupuesto (tras clave) como blob → object URL en el componente.
  proposalPdf: async (slug: string, token: string | null, fromHub?: boolean): Promise<Blob> => {
    const qs = fromHub ? '?from=hub' : '';
    const res = await fetch(`${BASE}/propuesta/${encodeURIComponent(slug)}/pdf${qs}`, {
      headers: token ? { 'x-proposal-token': token } : {},
    });
    if (!res.ok) throw new Error('No se pudo descargar el presupuesto.');
    return res.blob();
  },
  // ── Propuesta INTERNA (#360 · drawer del lead, Dirección) ────────────────────
  leadProposal: (id: string) =>
    req<{ proposal: LeadProposalState | null }>(`/leads/${id}/proposal`),
  // Crea el draft (idempotente). access_key en claro SOLO al crear (se muestra una vez).
  leadProposalCreate: (id: string, body?: { copy?: LeadProposalCopy }) =>
    req<{ proposal: LeadProposalState; created: boolean; access_key: string | null }>(
      `/leads/${id}/proposal`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  // Editar copy / project_name (naming manual) / valid_until del draft.
  leadProposalPatch: (
    id: string,
    body: {
      copy?: LeadProposalCopy;
      project_name?: string | null;
      valid_until?: string | null;
      // · #393 · imagen del hero (selector; por defecto la de serie umbral-madera-cal)
      hero_image_key?: string;
      hero_image_url?: string | null;
    },
  ) =>
    req<{ proposal: LeadProposalState | null }>(`/leads/${id}/proposal`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  leadProposalRegenerateKey: (id: string) =>
    req<{ ok: boolean; access_key: string }>(`/leads/${id}/proposal/regenerate-key`, {
      method: 'POST',
    }),
  // Enviar: valida naming+copy → 'sent', enciende módulo del hub, email con la clave.
  leadProposalSend: (id: string) =>
    req<{
      ok: boolean;
      proposal: LeadProposalState | null;
      hub_module_enabled: boolean;
      email_queued: boolean;
      email_skipped_reason: string | null;
      access_key: string | null;
    }>(`/leads/${id}/proposal/send`, { method: 'POST' }),
  listInvoices: (params?: { status?: string; cursor?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.cursor) q.set('cursor', params.cursor);
    if (params?.limit) q.set('limit', String(params.limit));
    // #390 · la lista incluye client_name (join en la API) para no enseñar UUIDs
    return req<ListResult<InvoicePublic & { client_name: string | null }>>(
      `/invoices?${q.toString()}`,
    );
  },
  getInvoice: (id: string) =>
    req<InvoicePublic & { lines: InvoiceLineRow[]; notes: string | null; concepto: string | null }>(
      `/invoices/${id}`,
    ),
  createInvoice: (body: InvoiceCreateInput) =>
    req<{ id: string; status: string }>('/invoices', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #322 · registrar/borrar un pago a cuenta sobre una factura (también emitida)
  addInvoiceAdvance: (
    id: string,
    body: { label?: string; amount_cents: number; paid_date?: string },
  ) =>
    req<{ id: string }>(`/invoices/${id}/advance-payments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteInvoiceAdvance: (id: string, apId: string) =>
    req<{ ok: boolean }>(`/invoices/${id}/advance-payments/${apId}`, { method: 'DELETE' }),
  // Ticket C · hitos del plan de pagos de un presupuesto, con importe/devengado/pendiente
  // (opción 2) y si ya están facturados. Alimenta el selector de "+ Nueva Factura" por hito.
  honorariosHitos: (calculationId: string) =>
    req<{
      calculation_id: string;
      project_id: string | null;
      status: string;
      presupuesto_full_number: string | null;
      total_cents: number;
      hitos: {
        hito_index: number;
        phase: string | null;
        percentage: number | null;
        amount_cents: number;
        devengado_cents: number;
        pendiente_cents: number;
        due_event: string | null;
        invoice_id: string | null;
        invoice_status: string | null;
      }[];
    }>(`/honorarios/${calculationId}/hitos`),
  // Ticket C · crea un BORRADOR de factura para un hito (traslada el subtotal del hito).
  createInvoiceFromHito: (body: {
    honorarios_calculation_id: string;
    hito_index: number;
    client_id?: string;
    tax_rate?: number;
  }) =>
    req<{
      invoice_id: string;
      series: string;
      status: string;
      hito_index: number;
      hito_amount_cents: number;
      hito_devengado_cents: number;
      hito_pendiente_cents: number;
      subtotal_cents: number;
      tax_cents: number;
      total_cents: number;
    }>('/invoices/from-hito', { method: 'POST', body: JSON.stringify(body) }),
  // Editar un borrador (admin · sin oferta previa). 409 si la factura ya está emitida.
  updateInvoice: (id: string, body: InvoiceUpdateInput) =>
    req<{ id: string; updated: boolean }>(`/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // #436 · reasignar el PROYECTO de una factura, también si ya está emitida. NO toca nada
  // fiscal (importe, número, fecha y cliente siguen siendo inmutables): solo la imputación,
  // que es lo que decide si el cliente la ve en el portal de ese proyecto.
  // 409 si el proyecto es de otro cliente, si es una certificación de obra, si es un borrador
  // o una anulada, o si es una rectificativa suelta (se mueve la factura, no el abono).
  // Las rectificativas de la factura VIAJAN CON ELLA: `credit_notes_moved` las nombra.
  reassignInvoiceProject: (id: string, body: { project_id: string | null; reason?: string }) =>
    req<{
      invoice_id: string;
      full_number: string | null;
      from_project_id: string | null;
      to_project_id: string | null;
      changed: boolean;
      credit_notes_moved: string[];
    }>(`/invoices/${id}/project`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Borrar un borrador (admin). 409 si la factura ya está emitida (usar rectificativa).
  deleteInvoice: (id: string) =>
    req<{ id: string; deleted: boolean }>(`/invoices/${id}`, { method: 'DELETE' }),
  issueInvoice: (id: string, body?: { issue_date?: string; due_days?: number }) =>
    req<{ invoice_id: string; full_number: string; total_cents: number }>(`/invoices/${id}/issue`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  // Gate de validación · PDF de la factura para revisar antes de emitir (URL absoluta para window.open)
  // #161 · `bank` elige qué cuenta de MUNYA (general | suplidos) aparece en el PDF.
  invoicePdfUrl: (id: string, bank?: 'general' | 'suplidos') =>
    `${BASE}/invoices/${id}/pdf${bank && bank !== 'general' ? `?bank=${bank}` : ''}`,
  // #200 · "Ver" de una factura EMITIDA · PDF definitivo con QR VERIFACTU (URL para window.open).
  invoiceIssuedPdfUrl: (id: string, bank?: 'general' | 'suplidos') =>
    `${BASE}/invoices/${id}/issued-pdf${bank && bank !== 'general' ? `?bank=${bank}` : ''}`,
  // #422 · cacheado (TTL arriba) + dedup en vuelo · ver invalidateClientsCache() arriba.
  listClients: (q?: string): Promise<{ items: ClientOption[] }> => {
    const key = q ?? '';
    if (
      clientsCache &&
      clientsCache.key === key &&
      Date.now() - clientsCache.at < CLIENTS_CACHE_TTL_MS
    ) {
      // Copia defensiva: si un consumidor ordenara el array in place, corrompería la caché
      // para todos los demás.
      return Promise.resolve({ items: [...clientsCache.items] });
    }
    const pending = clientsInFlight.get(key);
    if (pending) return pending;
    const gen = clientsCacheGen;
    // Anotación explícita: el callback de .finally() referencia `promise`, y sin el tipo
    // escrito TypeScript lo declara circular (implicit any).
    const promise: Promise<{ items: ClientOption[] }> = req<{ items: ClientOption[] }>(
      `/clients${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    )
      .then((data) => {
        if (gen === clientsCacheGen) clientsCache = { key, at: Date.now(), items: data.items };
        return data;
      })
      .finally(() => {
        if (clientsInFlight.get(key) === promise) clientsInFlight.delete(key);
      });
    clientsInFlight.set(key, promise);
    return promise;
  },

  // C9.3-ter · Certificaciones de obra (facturación quincenal con retención 5%)
  listCertificaciones: (projectId: string) =>
    req<{ items: CertificacionRow[]; retention_summary: RetentionSummary | null }>(
      `/projects/${projectId}/certificaciones`,
    ),
  createCertificacion: (
    projectId: string,
    body: {
      period_start: string;
      period_end: string;
      certified_subtotal_cents: number;
      retention_pct?: number;
      tax_rate?: number;
      concept?: string;
    },
  ) =>
    req<{
      full_number: string;
      certification_sequence: number;
      retention_cents: number;
      total_cents: number;
    }>(`/projects/${projectId}/certificaciones`, { method: 'POST', body: JSON.stringify(body) }),
  releaseRetention: (projectId: string) =>
    req<{ full_number: string; released_retention_cents: number; certifications_released: number }>(
      `/projects/${projectId}/certificaciones/release-retention`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // #355 · contrato GET /leads: por DEFECTO la API devuelve SOLO los stages activos
  // (new…negotiation) sin límite que los recorte; include_closed=true habilita
  // closed_won/closed_lost (vista Archivo). No forzamos limit desde el cliente.
  listLeads: (params?: {
    stage?: LeadStage;
    channel?: string;
    cursor?: string;
    limit?: number;
    include_closed?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.stage) q.set('stage', params.stage);
    if (params?.channel) q.set('channel', params.channel);
    if (params?.cursor) q.set('cursor', params.cursor);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.include_closed) q.set('include_closed', 'true');
    const qs = q.toString();
    return req<ListResult<LeadPublic>>(`/leads${qs ? `?${qs}` : ''}`);
  },
  getLead: (id: string) =>
    req<
      LeadPublic & {
        // #345 · payload jsonb con el contenido completo del apunte (briefing, reunión…)
        activities: {
          id: string;
          kind: string;
          occurred_at: string;
          summary: string;
          payload?: Record<string, unknown> | null;
        }[];
        contact_phone?: string;
        // #467 · null real cuando la nota se deja en blanco (la columna es nullable)
        qualification_notes?: string | null;
        location_description?: string;
        // #444 · ubicación estructurada del ENCARGO (distinta del domicilio del contacto)
        location_address?: {
          street?: string | null;
          postal_code?: string | null;
          city?: string | null;
          province?: string | null;
        } | null;
        lost_reason?: string;
        // #352 · contacto ligado (base A6) + tipología para el formulario de edición
        client_id?: string | null;
        typology_intent?: string | null;
        // #375 · carpeta OneDrive del lead + marca de archivado en frío (NAS)
        onedrive_folder_id?: string | null;
        onedrive_folder_name?: string | null;
        folder_cold_archived_at?: string | null;
      }
    >(`/leads/${id}`),
  createLead: (body: LeadCreate) =>
    req<{ id: string; stage: LeadStage; created_at: string }>('/leads', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #356 · registrar un lead YA GANADO legacy (Odoo / anterior a MunyaOS) con su proyecto.
  createLegacyWonLead: (body: LeadLegacyWonCreate) =>
    req<{ lead_id: string; project_id: string; client_id: string; idempotent: boolean }>(
      '/leads/legacy-won',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // #370 · "Ganado · Legacy" · marca un lead existente como ganado SOLO para métricas,
  // sin crear proyecto ni disparar firma/emails/magic-links. Opcional: enlazar un
  // proyecto ya existente (project_id).
  markLeadWonLegacy: (id: string, body?: { project_id?: string }) =>
    req<{ lead_id: string; project_id: string | null; already_won: boolean }>(
      `/leads/${id}/mark-won-legacy`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  // #352 · admite también LeadBasicUpdate (edición de datos básicos desde la ficha)
  updateLead: (id: string, body: LeadUpdate | LeadBasicUpdate) =>
    req<{ id: string; updated_fields: string[] }>(`/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // #375 · marca la carpeta del lead como archivada en frío (NAS). Dirección la mueve a mano
  // al cerrar el año; esto limpia onedrive_folder_id y activa la nota en la ficha.
  markLeadFolderColdArchived: (id: string) =>
    req<{
      ok: boolean;
      already: boolean;
      folder_cold_archived_at: string;
      folder_name: string | null;
    }>(`/leads/${id}/folder-cold-archived`, { method: 'POST' }),
  dashboardHome: () =>
    req<{
      projects_active: number;
      tickets_open: number;
      pending_receivable_cents: number;
      pending_receivable_overdue: number;
      leads_active: number;
      leads_in_proposal: number;
      jornada: { time: string; label: string; tag: string }[];
      // A12 V1.5
      today_events: {
        id: string;
        title: string;
        start_time: string | null;
        end_time: string | null;
        kind: string;
        description: string | null;
        online_meeting_url: string | null;
        project_id: string | null;
        attendees_count: number;
      }[];
      today_tasks: {
        id: string;
        title: string;
        project_id: string | null;
        project_name: string | null;
        priority: string;
        status: string;
        start_date: string | null;
        due_date: string | null;
      }[];
      highlights: {
        id: string;
        title: string;
        subtitle: string;
        severity: 'info' | 'warning' | 'danger';
        // #383 · enlace directo (recordatorios de seguimiento → ficha del lead)
        href?: string;
      }[];
    }>('/dashboard/home'),
  chatConversations: () =>
    req<{
      items: {
        id: string;
        kind: 'channel' | 'project_channel' | 'dm' | 'group_dm';
        name: string | null;
        description: string | null;
        last_body: string | null;
        last_at: string | null;
        unread_count: number;
      }[];
    }>('/chat/conversations'),
  chatMessages: (conversationId: string, cursor?: string) => {
    const q = new URLSearchParams();
    if (cursor) q.set('cursor', cursor);
    return req<{
      items: {
        id: string;
        body: string;
        created_at: string;
        edited_at: string | null;
        sender_user_id: string;
        sender_name: string | null;
        slash_command: string | null;
        mentions: string[];
      }[];
      next_cursor: string | null;
    }>(`/chat/conversations/${conversationId}/messages?${q}`);
  },
  chatSend: (conversationId: string, body: string, parentMessageId?: string) =>
    req<{
      message_id: string;
      slash_command: string | null;
      resolved_mentions: number;
      entity_refs_count: number;
    }>('/chat/messages', {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: conversationId,
        body,
        ...(parentMessageId ? { parent_message_id: parentMessageId } : {}),
      }),
    }),
  chatThread: (conversationId: string, parentMessageId: string) =>
    req<{
      parent: { id: string; body: string; created_at: string; sender_name: string | null };
      replies: {
        id: string;
        body: string;
        created_at: string;
        sender_user_id: string;
        sender_name: string | null;
      }[];
    }>(`/chat/conversations/${conversationId}/threads/${parentMessageId}`),
  chatRead: (conversationId: string) =>
    req<{ ok: boolean }>(`/chat/conversations/${conversationId}/read`, { method: 'POST' }),
  chatEdit: (messageId: string, body: string) =>
    req<{ id: string }>(`/chat/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),
  chatDelete: (messageId: string, reason?: string) =>
    req<{ id: string }>(`/chat/messages/${messageId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  // D5 § 7.5-bis · toggle pin conversación
  chatPinToggle: (conversationId: string) =>
    req<{ id: string; pinned_at: string | null }>(`/chat/conversations/${conversationId}/pin`, {
      method: 'POST',
    }),
  // D6 § 7.5-bis · mute con duración
  chatMute: (conversationId: string, duration: '1h' | '1d' | '1w' | 'indefinite' | 'off') =>
    req<{ id: string; muted_until: string | null }>(`/chat/conversations/${conversationId}/mute`, {
      method: 'POST',
      body: JSON.stringify({ duration }),
    }),
  // D3 § 7.5-bis · toggle reaction (add si no existe, remove si ya)
  chatReact: (messageId: string, emoji: string) =>
    req<{ id: string; emoji: string; action: 'added' | 'removed' }>(
      `/chat/messages/${messageId}/reactions`,
      { method: 'POST', body: JSON.stringify({ emoji }) },
    ),

  // F4 § 7.9 · Histórico por proveedor (cumplimiento plazos + incidencias)
  providersHistory: () => req<{ items: ProviderHistoryRow[] }>('/providers/history'),

  // M2 sem 3 · Dashboard contable + Bancos
  pnlMonthly: (year?: number) =>
    req<{
      year: number;
      months: {
        month: string;
        revenue_cents: number;
        expense_cents: number;
        gross_profit_cents: number;
        invoice_count: number;
        expense_count: number;
      }[];
      ytd: { revenue: number; expenses: number; result: number };
    }>(`/contabilidad/pnl-monthly${year ? `?year=${year}` : ''}`),
  bankStatements: () => req<{ items: BankStatementRow[] }>('/contabilidad/bank-statements'),
  bankStatementLines: (id: string) =>
    req<{ items: BankStatementLine[] }>(`/contabilidad/bank-statements/${id}/lines`),
  recurringExpenses: () => req<{ items: RecurringExpense[] }>('/contabilidad/recurring-expenses'),
  createRecurringExpense: (
    body: Omit<RecurringExpense, 'id' | 'created_at' | 'active'> & { active?: boolean },
  ) =>
    req<{ id: string }>('/contabilidad/recurring-expenses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // J § 7.11-bis · Remates / Punch list
  rematesList: (params: { project_id?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params.project_id) q.set('project_id', params.project_id);
    if (params.status) q.set('status', params.status);
    return req<{ items: RemateItem[] }>(`/remates?${q.toString()}`);
  },
  rematesCreate: (body: {
    project_id: string;
    subject: string;
    description?: string;
    responsible_name?: string;
    responsible_email?: string;
    estimated_resolution_date?: string;
    cost_cents?: number;
  }) => req<{ id: string }>('/remates', { method: 'POST', body: JSON.stringify(body) }),
  rematesUpdate: (
    id: string,
    body: Partial<{
      status: string;
      description: string;
      cost_cents: number;
      estimated_resolution_date: string;
      responsible_name: string;
    }>,
  ) =>
    req<{ id: string }>(`/remates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // J4 · Cierre proyecto
  closureReadiness: (projectId: string) =>
    req<{
      ready: boolean;
      total_punch_items: number;
      resolved_punch_items: number;
      pending_by_status: { open: number; in_progress: number; blocked: number };
      suggested_act_summary: string | null;
    }>(`/projects/${projectId}/closure-readiness`),
  closureAct: (projectId: string) =>
    req<{ id: string; code: string }>(`/projects/${projectId}/closure-act`, { method: 'POST' }),

  chatSearch: (conversationId: string, q: string) => {
    const p = new URLSearchParams({ q });
    return req<{
      items: {
        id: string;
        body: string;
        created_at: string;
        sender_user_id: string;
        sender_name: string | null;
        rank: number;
      }[];
    }>(`/chat/conversations/${conversationId}/search?${p}`);
  },
  chatUsers: () =>
    req<{ items: { user_id: string; display_name: string; upn: string }[] }>('/chat/users'),
  chatEmbedsResolve: (refs: { kind: string; id: string }[], conversationId?: string) =>
    req<{
      items: {
        kind: string;
        id: string;
        found: boolean;
        preview?: {
          title: string;
          subtitle?: string;
          status?: string;
          href?: string;
          badge?: string;
          image_url?: string;
        };
      }[];
    }>('/chat/embeds/resolve', {
      method: 'POST',
      body: JSON.stringify({
        refs,
        ...(conversationId ? { conversation_id: conversationId } : {}),
      }),
    }),
  // #202 · plan de sync masivo: escanea OneDrive y propone candidatos por proyecto (no importa nada)
  getOneDriveSyncPlan: () =>
    req<{ projects: OneDriveSyncProject[] }>('/selection/onedrive-sync-plan'),
  // #202/#369 · sync de selección/track de un proyecto desde OneDrive (server-side, solo
  // Dirección). ASÍNCRONO: el POST responde 202 { job_id } al instante (no espera al
  // download+parse del Excel grande → sin 502 del proxy) y la UI hace polling de getOneDriveImportJobs.
  importSelectionMasterOnedrive: (body: {
    project_id: string;
    onedrive_path: string;
    replace?: boolean;
    // · #439 · ENSAYO: lee el maestro entero, produce el informe completo y no escribe nada.
    dry_run?: boolean;
    // · #473 · «sé lo que hago»: el fichero es el de este proyecto / es el maestro vigente.
    confirm_source?: boolean;
  }) =>
    req<OneDriveImportAck>('/selection/import-master-onedrive', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #439 §4.2 · comprobación de precondiciones SIN importar (la UI la llama al elegir fichero)
  checkSelectionImportPreconditions: (body: {
    project_id: string;
    file_name: string;
    replace?: boolean;
    dry_run?: boolean;
    confirm_source?: boolean;
  }) =>
    req<ImportPreconditionResult>('/selection/import-preconditions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #439 · copias de seguridad de la selección de un proyecto y su restauración
  listSelectionImportBackups: (projectId: string) =>
    req<{ backups: SelectionImportBackup[] }>(
      `/selection/import-backups?project_id=${encodeURIComponent(projectId)}`,
    ),
  // El informe se DESCARGA (no se lee por JSON): esta URL es la que va en el enlace.
  // `.md` para pegar en un correo · `.html` para abrirlo de un doble clic.
  selectionImportReportUrl: (runId: string, ext: 'md' | 'html' = 'md') =>
    `${BASE}/selection/import-runs/${runId}/informe.${ext}`,
  restoreSelectionImportBackup: (backupId: string) =>
    req<{ restored: boolean; items: number; media: number; links: number; removed: number }>(
      `/selection/import-backups/${backupId}/restore`,
      { method: 'POST' },
    ),
  importTrackOnedrive: (body: { project_id: string; onedrive_path: string }) =>
    req<OneDriveImportAck>('/track/import-onedrive', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #369 · estado de los jobs de import de OneDrive de un proyecto (polling de la tabla de sync)
  getOneDriveImportJobs: (projectId: string) =>
    req<{ jobs: OneDriveImportJob[] }>(
      `/onedrive-import/jobs?project_id=${encodeURIComponent(projectId)}`,
    ),
  // Projects (M3)
  listProjects: (params?: { status?: string; typology?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.typology) q.set('typology', params.typology);
    q.set('limit', String(params?.limit ?? 50));
    return req<{
      items: {
        id: string;
        concept_name: string;
        status: string;
        typology: string;
        intervention: string;
        priority: string;
        client_id: string;
        client_name: string | null;
        city: string | null;
        area_interior_m2: number | null;
        total_fee_cents: number | null;
        estimated_delivery_date: string | null;
        started_at: string | null;
        phases_count: number;
        phases_done: number;
        tasks_open: number;
        tasks_done: number;
        invoices_count: number;
        billed_paid_cents: number;
        billed_pending_cents: number;
      }[];
      next_cursor: string | null;
    }>(`/projects?${q}`);
  },
  projectsSummary: () =>
    req<{ totals: { total: number; active: number; in_construction: number; completed: number } }>(
      '/projects/summary',
    ),
  // Acciones de proyecto · archivar / cancelar / borrar
  projectPatch: (id: string, body: { status?: string; notes?: string }) =>
    req<{ id: string; updated: string[] }>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  projectDelete: (id: string) =>
    req<{ id: string; deleted: true; concept_name: string }>(`/projects/${id}`, {
      method: 'DELETE',
    }),
  // Docs · provisionar LAS DOS carpetas del proyecto a demanda («Crear carpeta ahora»).
  // #444 punto 6 · clona la `000_CARPETA BASE` de cada árbol y aplica la convención
  // `NNN_NOMBRE_CIUDAD` con el número de la serie real del estudio.
  ensureProjectFolder: (projectId: string) =>
    req<{
      folder_id: string | null;
      folder_name: string | null;
      honcontratos_id: string | null;
      studio_number: number | null;
      created: boolean;
      onedrive: ProjectFolderProvision;
    }>(`/projects/${projectId}/ensure-onedrive-folder`, { method: 'POST', body: '{}' }),

  // A11 V1.5 · multi-firmantes de contratos · listar / añadir / quitar
  contractSignersList: (contractId: string) =>
    req<{
      items: {
        id: string;
        contact_id: string | null;
        contact_name: string | null;
        contact_email: string | null;
        role: string;
        signature_method: string;
        status: string;
        snapshot_name: string;
        snapshot_email: string | null;
        snapshot_nif: string | null;
        signed_at: string | null;
        created_at: string;
      }[];
    }>(`/contracts/${contractId}/signers`),

  // Las tres posiciones son OPCIONALES y hasta ahora ni se mandaban: la firma de un PDF se
  // colocaba en ninguna parte porque nadie llenaba estas columnas y nadie las leía. Ya las
  // lee el envío. `position_page` es 1-basada; x/y son fracción de la página (0..1) desde el
  // borde izquierdo y desde el superior. Si no van, el envío coloca la casilla al pie de la
  // última página y lo advierte.
  contractSignerAdd: (
    contractId: string,
    body: {
      contact_id: string;
      role?: string;
      signature_method?: 'otp' | 'cert';
      position_page?: number;
      position_x?: number;
      position_y?: number;
    },
  ) =>
    req<{ id: string }>(`/contracts/${contractId}/signers`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  contractSignerRemove: (contractId: string, signerId: string) =>
    req<{ id: string; deleted: boolean }>(`/contracts/${contractId}/signers/${signerId}`, {
      method: 'DELETE',
    }),

  // A14 V1.5 · crear tarea (desde TareasTab del proyecto o Calendario Design Team)
  createTask: (body: Record<string, unknown>) =>
    req<{ id: string }>('/tasks', { method: 'POST', body: JSON.stringify(body) }),
  // #386 · borrado completo de una tarea (subtareas/comentarios/tiempos caen en cascada)
  deleteTask: (id: string) =>
    req<{ ok: boolean; id: string }>(`/tasks/${id}`, { method: 'DELETE' }),

  // A15 V1.5 · solicitud de corrección de fichaje (flujo X3 · empleado solicita,
  // Dirección/Marina aprueba, queda registro inmutable).
  // #150 · notificaciones (campanita) · cada usuario ve solo las suyas
  notificationsList: () =>
    req<{
      items: {
        id: string;
        kind: string;
        title: string;
        body: string | null;
        cta_url: string | null;
        cta_label: string | null;
        related_kind: string | null;
        related_id: string | null;
        severity: string;
        read_at: string | null;
        created_at: string;
      }[];
      unread: number;
    }>('/notifications'),
  notificationRead: (id: string) =>
    req<{ status: string }>(`/notifications/${id}/read`, { method: 'POST' }),
  notificationsReadAll: () =>
    req<{ status: string }>('/notifications/read-all', { method: 'POST' }),
  // #358 · archivar (saca la notificación del listado · archived_at)
  notificationArchive: (id: string) =>
    req<{ status: string }>(`/notifications/${id}/archive`, { method: 'POST' }),
  notificationsArchiveAll: () =>
    req<{ status: string }>('/notifications/archive-all', { method: 'POST' }),

  fichajeAdjustmentCreate: (body: {
    target_date: string;
    proposed_kind: 'check_in' | 'check_out' | 'pause_start' | 'pause_end';
    proposed_at: string; // ISO datetime
    motive: string;
  }) =>
    req<{ id: string }>('/fichaje/adjustments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // A16 V1.5 · listado + aprobación/rechazo de correcciones (#142: aprueba solo Dirección)
  fichajeAdjustmentsList: (params?: { status?: string; mine?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.mine) q.set('mine', 'true');
    return req<{
      items: {
        id: string;
        user_id: string;
        user_name: string | null;
        target_date: string;
        proposed_kind: string;
        proposed_at: string;
        motive: string;
        status: string;
        created_at: string;
      }[];
    }>(`/fichaje/adjustments?${q.toString()}`);
  },

  fichajeAdjustmentApprove: (id: string, note?: string) =>
    req<{ id: string; status: string }>(`/fichaje/adjustments/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(note ? { note } : {}),
    }),

  fichajeAdjustmentReject: (id: string, note: string) =>
    req<{ id: string; status: string }>(`/fichaje/adjustments/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  // #66 · Admin modifica fichajes directamente con motivo obligatorio (RD 8/2019)
  fichajeAdminOverride: (body: {
    user_id: string;
    target_date: string;
    kind: 'check_in' | 'check_out' | 'pause_start' | 'pause_end';
    new_at: string; // ISO
    motive: string;
  }) =>
    req<{ id: string }>('/fichaje/admin-override', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // #397 · detalle de un rango de días: fichajes EFECTIVOS (con las correcciones aplicadas),
  // la cadena ORIGINAL y los overrides con motivo, autor y fecha.
  // Sin user_id devuelve los propios (cualquier interno); con user_id ajeno exige Dirección.
  fichajeDayDetail: (params: { from: string; to: string; user_id?: string }) => {
    const q = new URLSearchParams({ from: params.from, to: params.to });
    if (params.user_id) q.set('user_id', params.user_id);
    return req<{
      user_id: string;
      user_name: string | null;
      from: string;
      to: string;
      effective: { id: string; kind: string; occurred_at: string; corrected: boolean }[];
      original: { id: string; kind: string; occurred_at: string }[];
      overrides: {
        id: string;
        target_date: string;
        kind: string;
        action: string;
        new_at: string | null;
        motive: string;
        admin_user_id: string;
        admin_name: string | null;
        created_at: string;
      }[];
    }>(`/fichaje/day-detail?${q.toString()}`);
  },

  // #397 · lote ATÓMICO de operaciones de Dirección sobre un día (modificar / añadir / eliminar /
  // crear jornada). Motivo obligatorio · NO consume el cupo X3 del trabajador.
  fichajeAdminDayOperations: (body: {
    user_id: string;
    target_date: string;
    motive: string;
    operations: {
      op: 'set' | 'delete';
      kind: 'check_in' | 'check_out' | 'pause_start' | 'pause_end';
      at?: string; // ISO · obligatorio si op = 'set'
      motive?: string;
    }[];
  }) =>
    req<{
      ok: boolean;
      user_id: string;
      target_date: string;
      applied: number;
      override_ids: string[];
    }>('/fichaje/admin/day-operations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Resumen REAL de fichajes por usuario × día (Dirección) · time_clock_entries
  fichajeDailySummary: (params: { from: string; to: string; user_id?: string }) => {
    const q = new URLSearchParams({ from: params.from, to: params.to });
    if (params.user_id) q.set('user_id', params.user_id);
    return req<{
      from: string;
      to: string;
      rows: {
        user_id: string;
        display_name: string | null;
        day: string;
        presence_seconds: number;
        pause_seconds: number;
        // #362 · effective_seconds ya descuenta la pausa legal de 30 min cuando presence>0 y pause==0.
        effective_seconds: number;
        // Jornada teórica EFECTIVA del contrato para ese user/día (0 en viernes/finde/festivo/sin-contrato).
        expected_seconds: number;
        first_in: string | null;
        last_out: string | null;
        entry_count: number;
      }[];
    }>(`/fichaje/itss/daily-summary?${q.toString()}`);
  },

  getProject: (id: string) =>
    req<{
      id: string;
      concept_name: string;
      status: string;
      typology: string;
      intervention: string;
      priority: string;
      has_construction: boolean;
      client_id: string;
      address: Record<string, string | null>;
      area_interior_m2: number | null;
      area_exterior_m2: number | null;
      rooms_count: number | null;
      floors_count: number | null;
      low_purchasing_zone: boolean;
      lead_id: string | null;
      total_fee_cents: number | null;
      payment_plan: unknown;
      started_at: string | null;
      estimated_delivery_date: string | null;
      closed_at: string | null;
      notes: string | null;
      client_name: string | null;
      client_email: string | null;
      phases: {
        id: string;
        phase: string;
        position: number;
        status: string;
        estimated_start: string | null;
        estimated_end: string | null;
        deliverables_count: number;
        deliverables_done: number;
        payment_amount_cents: number | null;
      }[];
      team: {
        user_id: string;
        role: string;
        display_name: string | null;
        upn: string | null;
        assigned_at: string;
      }[];
      tasks: {
        id: string;
        title: string;
        status: string;
        priority: string;
        assigned_to: string | null;
        due_date: string | null;
        completed_at: string | null;
      }[];
      invoices: {
        id: string;
        full_number: string | null;
        status: string;
        total_cents: number;
        currency: string;
        issue_date: string | null;
        due_date: string | null;
      }[];
      // Ticket A · gastos de proyecto + suplidos asociados
      expenses: {
        id: string;
        expense_date: string;
        amount_cents: number;
        currency: string;
        category: string;
        description: string;
        supplier_name: string | null;
        invoice_ref: string | null;
        is_suplido: boolean;
        invoice_pdf_url: string | null;
        invoice_pdf_name: string | null;
        paid: boolean;
        paid_at: string | null;
      }[];
    }>(`/projects/${id}`),
  createProject: (body: {
    concept_name: string;
    client_id: string;
    typology: string;
    intervention?: string;
    has_construction?: boolean;
    priority?: string;
  }) => req<{ id: string }>('/projects', { method: 'POST', body: JSON.stringify(body) }),

  // Project templates · § 7.5
  listProjectTemplates: (language: 'es' | 'en' | 'de' = 'es') =>
    req<{
      items: {
        id: string;
        slug: string;
        name: string;
        description: string;
        typology: string;
        intervention: string;
        has_construction: boolean;
        language: string;
        phases_to_create: string[];
        payment_plan_template: { phase: string; percentage: number; due_event: string }[];
        studio_margin_default: string;
        suggested_team_roles: string[];
        onedrive_extras: string[];
        version: string;
      }[];
    }>(`/project-templates?language=${language}`),
  createProjectFromTemplate: (body: {
    template_id: string;
    client_id?: string;
    lead_id?: string;
    concept_name: string;
    // #444 · obligatoria en el alta: calle y número, código postal y ciudad. De aquí salen
    // `projects.address` y la etiqueta compuesta que da nombre a la carpeta de OneDrive.
    location: { street: string; postal_code: string; city: string; province?: string };
    area_interior_m2?: number;
    area_exterior_m2?: number;
    rooms_count?: number;
    floors_count?: number;
    team_user_ids?: string[];
    notes?: string;
  }) =>
    req<{
      id: string;
      sequence_number: number;
      // #444 · número de la SERIE REAL del estudio (el de la carpeta), distinto del correlativo
      // de la tabla. null si el proyecto no lleva carpeta (demo/interno).
      studio_number: number | null;
      template_slug: string;
      // #444 punto 8 · resultado de la creación de LAS DOS carpetas. Si `ok` es false, el alta
      // NO puede terminar en verde: la ficha se ha quedado sin la referencia.
      onedrive: ProjectFolderProvision;
      next_steps: string[];
    }>('/projects/from-template', { method: 'POST', body: JSON.stringify(body) }),

  // #444 punto 5 · nombre de las DOS carpetas que se van a crear, ANTES de crear nada.
  // Solo lectura: no reserva número ni toca OneDrive.
  projectFolderPreview: (params: { concept_name: string; city: string }) =>
    req<{
      studio_number: number;
      folder_name: string;
      proyectos_path: string;
      honcontratos_path: string;
      provisional: boolean;
    }>(
      `/projects/folder-preview?concept_name=${encodeURIComponent(params.concept_name)}&city=${encodeURIComponent(params.city)}`,
    ),

  // Honorarios (M3.2)
  honorariosActiveParams: () =>
    req<{
      id: string;
      version: string;
      description: string;
      multiplier_vivienda: number;
      multiplier_tienda: number;
      multiplier_restaurante: number;
      multiplier_hotel: number;
      multiplier_stand_feria: number;
      multiplier_decoracion: number;
      multiplier_reforma: number;
      multiplier_reforma_integral: number;
      multiplier_obra_nueva: number;
      multiplier_rehabilitacion: number;
      multiplier_feria: number;
      tasks: {
        code: string;
        phase: string;
        task_type: string;
        title: string;
        position: number;
        default_required: 'si' | 'no' | 'fijo';
        default_workload: string;
      }[];
    }>('/honorarios/parameter-sets/active'),
  // A9 V1.5 · admin de parámetros · GET y PATCH completos (lectura granular del set activo)
  honorariosParameterSet: (id = 'active') =>
    req<HonorariosParameterSetFull>(`/honorarios/parameter-sets/${id}`),
  honorariosParameterSetPatch: (id: string, body: Partial<HonorariosParameterSetEditable>) =>
    req<{ id: string; updated: string[] }>(`/honorarios/parameter-sets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  honorariosTasks: () =>
    req<{
      parameter_set_id: string;
      version: string;
      items: {
        code: string;
        phase: string;
        task_type: string;
        title: string;
        position: number;
        default_required: 'si' | 'no' | 'fijo';
        default_workload: string;
      }[];
    }>('/honorarios/tasks'),
  honorariosCalculate: (body: HonorariosCalcInput) =>
    req<HonorariosCalcResult>('/honorarios/calculate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  honorariosCreateCalculation: (body: HonorariosCalcInput) =>
    req<HonorariosCalcResult & { id: string }>('/honorarios/calculations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  honorariosCompareCalculations: (ids: string[]) =>
    req<{
      items: {
        id: string;
        parameter_set_version: string;
        model: 'full' | 'consulta';
        area_weighted_m2: string;
        subtotal_cents: string;
        tax_cents: string;
        total_cents: string;
        payment_plan: {
          phase: string;
          percentage: number;
          amount_cents: number;
          due_event: string;
        }[];
        status: string;
        created_at: string;
      }[];
    }>(`/honorarios/calculations/compare?ids=${ids.join(',')}`),
  // Contracts (M3.3)
  createContract: (body: {
    honorarios_calculation_id: string;
    language?: 'es' | 'en' | 'de';
    client_id?: string;
    // #412 P4 · confirmar promover a vigente si el lead tenía otro vigente
    promote_to_current?: boolean;
  }) =>
    req<{
      id: string;
      contract_number: string;
      // #170 p9 · dirección física con la que el contrato identifica el proyecto
      project_address?: string;
      // true si había plantilla activa y el cuerpo se renderizó con los datos reales
      template_rendered?: boolean;
    }>('/contracts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listContracts: (params?: { lead_id?: string; project_id?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.lead_id) q.set('lead_id', params.lead_id);
    if (params?.project_id) q.set('project_id', params.project_id);
    if (params?.status) q.set('status', params.status);
    return req<{
      items: {
        id: string;
        contract_number: string | null;
        project_id: string | null;
        lead_id: string | null;
        honorarios_calculation_id: string;
        total_cents: number;
        status: string;
        docuseal_submission_id: string | null;
        signed_at: string | null;
        // #170 p9 · dirección física con la que el contrato identifica el proyecto
        project_address_label: string | null;
        signature_certificate_path: string | null;
        created_at: string;
      }[];
    }>(`/contracts?${q}`);
  },
  getContract: (id: string) =>
    req<{
      id: string;
      contract_number: string | null;
      status: string;
      docuseal_submission_id: string | null;
      signed_at: string | null;
      total_cents: number;
      tax_cents: number;
      events: {
        id: string;
        kind: string;
        occurred_at: string;
        docuseal_event_id: string | null;
        actor_email: string | null;
        user_agent: string | null;
      }[];
    }>(`/contracts/${id}`),
  // Los tres datos del contacto son OPCIONALES: quien firma son los firmantes del contrato
  // (contract_signers), y esto es sólo el respaldo para el contrato que aún no tiene ninguno.
  sendContractToSignature: (
    id: string,
    body: {
      client_email?: string;
      client_phone?: string;
      signer_name?: string;
      docuseal_template_id?: string | number;
      expire_days?: number;
    },
  ) =>
    req<{
      submission_id: number;
      status: string;
      signers_sent?: number;
      template_source?: string;
      warning?: string;
      /** El enlace de firma de cada firmante · sin SMTP es lo ÚNICO que llega a nadie. */
      signing_links?: { name: string | null; email: string | null; url: string }[];
    }>(`/contracts/${id}/send-to-signature`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Los enlaces de firma de la última solicitud. Existen porque DocuSeal NO tiene servidor de
   * correo: la invitación no sale y este enlace es la única forma de que el firmante llegue
   * al documento. Antes se perdía en cuanto se cerraba el aviso de «enviado».
   */
  contractSigningLinks: (id: string) =>
    req<{
      items: { name: string | null; email: string | null; role: string | null; url: string }[];
      sent_at: string | null;
    }>(`/contracts/${id}/signing-links`),
  cancelContract: (id: string, reason: string) =>
    req<{ id: string; status: string }>(`/contracts/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  refreshDocusealStatus: (id: string) =>
    req<{ docuseal_status: string; reconciled?: string }>(`/contracts/${id}/refresh-docuseal`, {
      method: 'POST',
    }),

  // #443 · colgar el contrato de un lead o de un proyecto (o desvincularlo con null).
  // Un contrato subido a mano nacía huérfano y no había forma de vincularlo después.
  updateContractLinks: (
    id: string,
    body: { lead_id?: string | null; project_id?: string | null },
  ) =>
    req<{
      id: string;
      lead_id: string | null;
      project_id: string | null;
      lead_derived_from_project: boolean;
    }>(`/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // ALIAS HEREDADO de sendContractToSignature. Ya no hay «otro camino» para reintentar: el
  // servidor lo resuelve con el mismo dispatchContractSignature. Ninguna pantalla lo llama
  // (había dos botones de envío y se quedó el verde); se conserva por si alguien lo tuviera
  // guardado. Para (re)enviar, usa sendContractToSignature.
  retryManualSignature: (id: string, body?: { title?: string; expire_days?: number }) =>
    req<{
      id: string;
      docuseal_submission_id: number;
      warning?: string;
      signing_links?: { name: string | null; email: string | null; url: string }[];
    }>(`/contracts/${id}/manual-signature/retry`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  // TRACK (M4.1-M4.3)
  listTrack: (params?: {
    project_id?: string;
    status?: string;
    priority?: string;
    area?: string;
    assignee_user_id?: string;
    tag?: string;
    mine?: boolean;
    limit?: number;
    q?: string;
    open_only?: boolean;
    /**
     * · #453 §5.2 · enlace de VUELTA desde la Selección: tickets que referencian este artículo.
     * El servidor mira la tabla de referencias (varias por ticket) y también la columna legado.
     */
    selection_item_id?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', params.project_id);
    if (params?.status) q.set('status', params.status);
    if (params?.priority) q.set('priority', params.priority);
    if (params?.area) q.set('area', params.area);
    if (params?.assignee_user_id) q.set('assignee_user_id', params.assignee_user_id);
    if (params?.tag) q.set('tag', params.tag);
    if (params?.mine) q.set('mine', 'true');
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.q) q.set('q', params.q);
    if (params?.open_only) q.set('open_only', 'true');
    if (params?.selection_item_id) q.set('selection_item_id', params.selection_item_id);
    return req<{ items: TrackItemRow[] }>(`/track?${q}`);
  },
  trackSummary: (project_id?: string) => {
    const q = project_id ? `?project_id=${project_id}` : '';
    return req<{
      totals: {
        total: number;
        pendiente: number;
        en_progreso: number;
        en_revision: number;
        procesado: number;
        overdue: number;
      };
    }>(`/track/summary${q}`);
  },
  getTrack: (id: string) =>
    req<
      TrackItemDetail & {
        parent_item_id: string | null;
        thread_root_id: string | null;
        thread: {
          id: string;
          sequence_number: number;
          subject: string;
          status: string;
          priority: string;
          created_at: string;
          closed_at: string | null;
          // · #453 §5.3 · sin el padre de cada elemento el hilo es una lista plana
          parent_item_id: string | null;
          display_code: string | null;
          closed_by_name: string | null;
        }[];
        // · #453 §5.2 · referencias cruzadas del ticket (varias, dos tipos)
        references: TrackItemReference[];
      }
    >(`/track/${id}`),
  // · #453 §5.2 · añadir/retirar una referencia cruzada
  addTrackReference: (id: string, body: { ref_type: TrackRefType; ref_id: string }) =>
    req<{ added: number }>(`/track/${id}/references`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeTrackReference: (refId: string) =>
    req<{ id: string; deleted: boolean }>(`/track/references/${refId}`, { method: 'DELETE' }),
  createTrack: (body: TrackCreate & { parent_item_id?: string }) =>
    req<{ id: string; sequence_number: number }>('/track', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTrack: (id: string, body: TrackUpdate) =>
    req<{ id: string; updated: string[] }>(`/track/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addTrackComment: (id: string, body: string) =>
    req<{ id: string }>(`/track/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  trackSpaces: (project_id?: string) => {
    const q = project_id ? `?project_id=${project_id}` : '';
    return req<{ items: { id: string; code: string; label: string; position: number }[] }>(
      `/track/spaces${q}`,
    );
  },

  // Selección (M4.4-M4.5)
  selectionProviders: () =>
    req<{
      items: {
        id: string;
        name: string;
        pricing_model: string;
        default_discount_percent: string;
        website: string | null;
        // · #411 W4b · p6 · 'pending' (alta de Diseño sin economía) | 'approved'
        review_status?: 'pending' | 'approved';
      }[];
    }>('/selection/providers'),
  // · #411 W4b · p6 · aprobar un proveedor pendiente (solo Dirección/Gestión); fija economía opcional
  approveProvider: (
    id: string,
    body?: { default_discount_percent?: number; default_markup_percent?: number },
  ) =>
    req<{ id: string; review_status: string }>(`/selection/providers/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  listSelection: (
    projectId: string,
    params?: { category?: string; space_id?: string; order_status?: string; provider_id?: string },
  ) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.space_id) q.set('space_id', params.space_id);
    if (params?.order_status) q.set('order_status', params.order_status);
    if (params?.provider_id) q.set('provider_id', params.provider_id);
    return req<{ items: SelectionItemComputed[] }>(`/projects/${projectId}/selection?${q}`);
  },
  // · #453 §5.2 · MODO LIGERO del mismo endpoint (`light=1`): ocho resultados con lo justo para
  // el buscador de referencias del Track. Aditivo: no cambia lo que recibe listSelection().
  searchSelectionLight: (projectId: string, params?: { q?: string; limit?: number }) => {
    const p = new URLSearchParams({ light: '1' });
    if (params?.q?.trim()) p.set('q', params.q.trim());
    p.set('limit', String(params?.limit ?? 20));
    return req<{ items: SelectionLightItem[] }>(`/projects/${projectId}/selection?${p}`);
  },
  selectionSummary: (projectId: string) =>
    req<{
      // · #449 §5 · los campos de SelectionPurchaseSplit llegan en el MISMO objeto para que
      // pasen por el mismo filtro por rol que ya esconde el margen (`beneficio_cents` se
      // borra en servidor cuando el rol no ve márgenes, y por eso es opcional aquí).
      totals: {
        total: number;
        principales: number;
        pendientes_compra: number;
        en_proceso: number;
        recibidos: number;
        total_munya_cents: string;
        beneficio_cents?: string;
      } & SelectionPurchaseSplit;
    }>(`/projects/${projectId}/selection/summary`),
  createSelectionItem: (body: SelectionItemCreate) =>
    req<{ id: string; identifier: string }>('/selection/items', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #482 · sobre una posición YA APROBADA por el cliente, `change_reason` es obligatorio
  // (mínimo 10 caracteres) y NO es un campo del artículo: es el motivo que él leerá en su
  // portal. Sin él, el servidor responde 409 «Falta el motivo del cambio».
  updateSelectionItem: (
    id: string,
    body: Partial<SelectionItemCreate> & { change_reason?: string },
  ) =>
    req<{ id: string }>(`/selection/items/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSelectionItem: (id: string) =>
    req<{ id: string; deleted: boolean }>(`/selection/items/${id}`, { method: 'DELETE' }),
  // · #411 O3 p4 · duplicar un artículo (misma u otra estancia del MISMO proyecto). El servidor
  // copia la economía completa y las imágenes como media NUEVAS. body.space_id: uuid = otra
  // estancia, null = sin ubicación, ausente = misma estancia del origen.
  duplicateSelectionItem: (id: string, body?: { space_id?: string | null }) =>
    req<SelectionMutationResult>(`/selection/items/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  // · #411 O3 p4 · alta desde el catálogo de productos. Si el producto salió de una selección viva
  // hereda su economía y medias (delegando en el duplicado cross-proyecto); si no, mapea los planos.
  createSelectionItemFromProduct: (body: {
    product_id: string;
    project_id: string;
    space_id?: string | null;
  }) =>
    req<SelectionMutationResult>('/selection/items/from-product', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #411 O3 p4 · buscador del catálogo (dedup=1 agrupa por nombre+fabricante para no ofrecer
  // N veces la misma silla que el trigger de alta automática crea por cada línea de selección)
  searchProducts: (q: string) => {
    const p = new URLSearchParams({ dedup: '1' });
    if (q.trim()) p.set('q', q.trim());
    return req<{ items: ProductCatalogItem[] }>(`/products?${p.toString()}`);
  },
  // · #411 p2 · reordenar artículos principales dentro de una estancia (drag&drop interno).
  // space_id null = grupo «Sin ubicación». ordered_ids = ids de los principales en el nuevo orden.
  reorderSelection: (projectId: string, body: { space_id: string | null; ordered_ids: string[] }) =>
    req<{ reordered: number }>(`/projects/${projectId}/selection/reorder`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #411 p7b · alta de ubicación desde los desplegables internos de Selección (endpoint #231
  // ya deduplica el code y admite todo interno, Diseño incluido). room_group opcional.
  createProjectSpace: (projectId: string, body: { label: string; room_group?: string }) =>
    req<{ id: string; code: string; label: string }>(`/projects/${projectId}/spaces`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #411 p8 · reordenar estancias: PATCH de project_spaces.position (swap con la vecina).
  // · #411 W5 · group_id asigna la estancia a un grupo (nombre libre); null = sin grupo.
  patchProjectSpace: (
    projectId: string,
    spaceId: string,
    body: {
      position?: number;
      label?: string;
      room_group?: string | null;
      group_id?: string | null;
    },
  ) =>
    req<{ id: string }>(`/projects/${projectId}/spaces/${spaceId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // · #411 W5 · p7a · grupos de ubicaciones (nombre libre) del proyecto: GRUPO → ESTANCIA → ítems
  listSpaceGroups: (projectId: string) =>
    req<{ items: { id: string; name: string; position: number }[] }>(
      `/projects/${projectId}/space-groups`,
    ),
  createSpaceGroup: (projectId: string, name: string) =>
    req<{ id: string }>(`/projects/${projectId}/space-groups`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  patchSpaceGroup: (
    projectId: string,
    groupId: string,
    body: { name?: string; position?: number },
  ) =>
    req<{ id: string }>(`/projects/${projectId}/space-groups/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteSpaceGroup: (projectId: string, groupId: string) =>
    req<{ id: string; deleted: boolean }>(`/projects/${projectId}/space-groups/${groupId}`, {
      method: 'DELETE',
    }),

  // Fichaje (M4.6)
  fichajeClock: (
    kind: 'check_in' | 'check_out' | 'pause_start' | 'pause_end',
    // C2b · coords opcionales desde la app iOS (el backend las descarta sin opt-in L5)
    loc?: { location_lat: number; location_lng: number },
  ) =>
    req<{ id: string; signature_hash: string }>('/fichaje/clock', {
      method: 'POST',
      body: JSON.stringify({ kind, ...(loc ?? {}) }),
    }),
  fichajeGeoConsent: () =>
    req<{
      geo_fichaje_opt_in: boolean;
      geo_fichaje_consent_at: string | null;
      geo_fichaje_revoked_at: string | null;
    }>('/fichaje/geo-consent'),
  fichajeGeoConsentSet: (opt_in: boolean) =>
    req<{ ok: boolean; opt_in: boolean }>('/fichaje/geo-consent', {
      method: 'PATCH',
      body: JSON.stringify({ opt_in }),
    }),
  fichajeMe: () =>
    req<{
      last_entry: { kind: string; occurred_at: string } | null;
      today: {
        presence_minutes: number;
        pause_minutes: number;
        effective_minutes: number;
        is_open: boolean;
      };
    }>('/fichaje/me'),
  fichajeEntries: (params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    return req<{
      items: { id: string; kind: string; occurred_at: string; signature_hash: string }[];
    }>(`/fichaje/entries?${q}`);
  },
  fichajeVerifyIntegrity: () =>
    req<{ ok: boolean; total: number; first_broken_entry_id?: string }>(
      '/fichaje/verify-integrity',
      { method: 'POST' },
    ),

  // Equipo · alta/baja + sync licencias M365 (0115)
  teamSetActive: (user_id: string, active: boolean) =>
    req<{ user_id: string; updated: string[] }>(`/team/${user_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),
  // #465 · identidad PÚBLICA de la persona (retrato, nombre, cargo/área por idioma,
  // visibilidad y orden). Endpoint aparte del PATCH de rol/alta-baja a propósito: una cosa son
  // los permisos y otra la cara que el estudio le enseña a un cliente.
  teamPublicIdentityUpdate: (
    user_id: string,
    body: {
      portrait_url?: string | null;
      public_name?: string | null;
      public_role?: Record<string, string>;
      public_area?: Record<string, string>;
      show_in_client_pieces?: boolean;
      sort_order?: number;
    },
  ) =>
    req<{ user_id: string; updated: string[] }>(`/team/${user_id}/public-identity`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  teamSyncLicenses: (dryRun = false) =>
    req<{
      ok: boolean;
      error?: string;
      sku_part: string;
      licensed_count: number;
      activated: string[];
      deactivated: string[];
      unchanged: number;
      licensed_not_in_munya: string[];
      dry_run: boolean;
    }>(`/team/sync-licenses${dryRun ? '?dry_run=1' : ''}`, { method: 'POST' }),

  // Calendario Design Team (M4.7)
  teamCalendar: (from: string, to: string, user_id?: string) => {
    const q = new URLSearchParams({ from, to });
    if (user_id) q.set('user_id', user_id);
    return req<{
      items: {
        id: string;
        user_id: string;
        entry_date: string;
        jornada: string;
        hours_planned: string | null;
        project_id: string | null;
        notes: string | null;
        vacation_number: number | null;
        is_carry_over: boolean;
        user_name: string | null;
        project_name: string | null;
      }[];
    }>(`/team-calendar?${q}`);
  },
  teamCalendarSet: (body: {
    user_id: string;
    entry_date: string;
    jornada: string;
    hours_planned?: number;
    project_id?: string;
    notes?: string;
    vacation_number?: number;
    is_carry_over?: boolean;
  }) => req<{ id: string }>('/team-calendar', { method: 'POST', body: JSON.stringify(body) }),

  // #256 · roster del equipo (activos) para la rejilla de Vacaciones de equipo
  teamRoster: () =>
    req<{ users: { user_id: string; display_name: string; role: string }[] }>(
      '/team-calendar/roster',
    ),

  // Team calendar v2 · § 7.12-bis · #239 · days = rango (7 semana, 31 mes, 93 3m, 186 6m)
  teamCalendarPivot: (week_start: string, days?: number) =>
    req<{
      week_start: string;
      users: { user_id: string; display_name: string; upn: string; role: string }[];
      entries: Record<
        string,
        Record<
          string,
          {
            user_id: string;
            entry_date: string;
            schedule_type: string;
            start_time: string | null;
            end_time: string | null;
            is_remote: boolean;
            extra_hours: string | null;
            deficit_hours: string | null;
            vacation_day_number: number | null;
            vacation_from_year: number | null;
            is_half_day: boolean;
            is_company_shutdown: boolean;
            holiday_scope: string | null;
            planned_text: string | null;
            executed_text: string | null;
            related_project_ids: string[];
          }
        >
      >;
      events: {
        id: string;
        event_date: string;
        end_date: string | null;
        start_time: string | null;
        end_time: string | null;
        kind: string;
        event_type: string;
        title: string;
        description: string | null;
        online_meeting_url: string | null;
        delivery_id: string | null;
        project_id: string | null;
        attendee_user_ids: string[];
        confidence_level: string;
        // #401 · el panel de evento los necesita para decidir si ofrece editar/cancelar
        created_by: string | null;
        outlook_event_id: string | null;
      }[];
    }>(`/team-calendar/pivot?week_start=${week_start}${days ? `&days=${days}` : ''}`),

  teamCalendarUpsertV2: (body: {
    user_id: string;
    entry_date: string;
    schedule_type: string;
    start_time?: string;
    end_time?: string;
    is_remote?: boolean;
    extra_hours?: number;
    deficit_hours?: number;
    is_compensated?: boolean;
    vacation_day_number?: number;
    vacation_from_year?: number;
    is_half_day?: boolean;
    is_company_shutdown?: boolean;
    holiday_scope?: string;
    planned_text?: string;
    executed_text?: string;
    notes?: string;
    intra_day_events?: { type: string; start: string; end: string; note?: string }[];
  }) =>
    req<{
      id: string;
      extracted_project_ids: string[];
      code_matches: { code: string; project_id: string | null; concept_name: string | null }[];
    }>('/team-calendar/v2/entries', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  teamCalendarBalance: (params: { user_id?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params.user_id) q.set('user_id', params.user_id);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    return req<{
      items: {
        user_id: string;
        user_name: string | null;
        week_start: string;
        year: number;
        extra_total: string;
        deficit_total: string;
        balance: string;
        vacation_days: string;
        remote_days: string;
      }[];
    }>(`/team-calendar/balance?${q}`);
  },

  teamCalendarEventCreate: (body: {
    event_date: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    kind?: string;
    event_type: string;
    title: string;
    description?: string;
    delivery_id?: string;
    project_id?: string;
    attendee_user_ids?: string[];
    confidence_level?: string;
  }) =>
    req<{ id: string; m365_invited: number; outlook_event_id: string | null }>(
      '/team-calendar/events',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  // #401 · editar / cancelar / sumarse a una reunión ya convocada.
  // `outlook_synced` = si la invitación de Outlook se pudo actualizar; false cuando el
  // evento no tiene espejo en Graph (creado antes de #401) o Graph no respondió.
  teamCalendarEventPatch: (
    id: string,
    body: {
      event_date?: string;
      end_date?: string | null;
      start_time?: string | null;
      end_time?: string | null;
      kind?: string;
      event_type?: string;
      title?: string;
      description?: string | null;
      delivery_id?: string;
      project_id?: string;
      attendee_user_ids?: string[];
      confidence_level?: string;
    },
  ) =>
    req<{ id: string; outlook_synced: boolean }>(`/team-calendar/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  teamCalendarEventDelete: (id: string) =>
    req<{ id: string; cancelled: boolean; outlook_synced: boolean }>(
      `/team-calendar/events/${id}`,
      { method: 'DELETE' },
    ),

  teamCalendarEventJoin: (id: string) =>
    req<{ id: string; already: boolean; outlook_synced: boolean }>(
      `/team-calendar/events/${id}/join`,
      { method: 'POST' },
    ),

  // Leave requests + employee contracts · § 7.12-bis sub-tanda 8B
  // #79 · Recordatorios de seguimiento de leads
  leadFollowUpsList: (leadId: string) =>
    req<{ items: LeadFollowUp[] }>(`/leads/${leadId}/follow-ups`),
  leadFollowUpCreate: (
    leadId: string,
    body: { due_date: string; note: string; notify_user_id?: string | null },
  ) =>
    req<{ id: string }>(`/leads/${leadId}/follow-ups`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #428 · editar un recordatorio existente sin borrarlo y reescribirlo
  leadFollowUpUpdate: (
    leadId: string,
    fid: string,
    body: { due_date?: string; note?: string; notify_user_id?: string | null },
  ) =>
    req<LeadFollowUp>(`/leads/${leadId}/follow-ups/${fid}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  leadFollowUpSetStatus: (fid: string, status: 'pending' | 'done' | 'cancelled') =>
    req<{ id: string; status: string }>(`/lead-follow-ups/${fid}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  leadFollowUpDelete: (fid: string) =>
    req<{ ok: boolean }>(`/lead-follow-ups/${fid}`, { method: 'DELETE' }),

  // #80/#448 · Videollamadas con el lead. El alta crea el evento Teams e invita al lead; el
  // PATCH REPROGRAMA EL MISMO evento (conserva el enlace de Teams y manda la invitación
  // actualizada) y el DELETE lo cancela en Outlook avisando al lead sin borrar la fila.
  leadMeetings: (leadId: string) => req<LeadMeetingsResponse>(`/leads/${leadId}/meetings`),
  leadMeetingCreate: (leadId: string, body: { subject: string; start: string; end: string }) =>
    req<{ meeting: LeadMeeting; became_current: boolean }>(`/leads/${leadId}/meetings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  leadMeetingUpdate: (
    leadId: string,
    mid: string,
    body: { subject?: string; start?: string; end?: string },
  ) =>
    req<{ meeting: LeadMeeting }>(`/leads/${leadId}/meetings/${mid}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  leadMeetingCancel: (leadId: string, mid: string, body?: { reason?: string }) =>
    req<{
      meeting: LeadMeeting;
      promoted_meeting_id: string | null;
      /** false = la cita no tenía evento en Outlook; al lead NO se le ha avisado. */
      lead_notified?: boolean;
      already_cancelled?: boolean;
    }>(`/leads/${leadId}/meetings/${mid}`, {
      method: 'DELETE',
      body: JSON.stringify(body ?? {}),
    }),
  leadMeetingSetCurrent: (leadId: string, mid: string) =>
    req<{ ok: boolean; changed: boolean; meeting: LeadMeeting }>(
      `/leads/${leadId}/meetings/${mid}/set-current`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // #83 · Subir contenido que el lead facilita por email / WhatsApp a la ficha del lead.
  leadBriefingNote: (
    leadId: string,
    body: { source: 'email' | 'whatsapp' | 'manual'; content: string },
  ) =>
    req<{ id: string }>(`/leads/${leadId}/briefing-note`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // #431 · `revocable` acota a los teletrabajos aprobados que TODAVÍA se pueden revocar (fecha
  // no cumplida). Quién decide qué es "cumplido" es la API, no la pantalla.
  leaveRequestsList: (params?: { status?: string; user_id?: string; revocable?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.user_id) q.set('user_id', params.user_id);
    if (params?.revocable) q.set('revocable', 'true');
    return req<{
      items: {
        id: string;
        user_id: string;
        user_name: string | null;
        upn: string | null;
        kind: string;
        from_date: string;
        to_date: string;
        is_half_day: boolean;
        from_time: string | null;
        to_time: string | null;
        justificante_url: string | null;
        fiscal_year: number | null;
        is_carry_over: boolean;
        reason: string | null;
        status: string;
        requested_at: string;
        decided_at: string | null;
        decided_by: string | null;
        decider_name: string | null;
        decision_notes: string | null;
        detected_conflicts: { kind: string; date: string; label: string }[];
        calendar_entry_ids: string[];
      }[];
    }>(`/leave-requests?${q}`);
  },

  // #331 · vacaciones/ausencias aprobadas de todo el equipo (nombre + rango) para la vista anual
  teamVacationsList: (params?: { year?: number }) => {
    const q = new URLSearchParams();
    if (params?.year) q.set('year', String(params.year));
    return req<{
      items: {
        user_id: string;
        user_name: string | null;
        kind: string;
        from_date: string;
        to_date: string;
      }[];
    }>(`/team-vacations?${q}`);
  },

  leaveRequestCreate: (body: {
    user_id?: string;
    kind?: 'vacation' | 'unpaid' | 'compensation_day' | 'personal' | 'teletrabajo';
    from_date: string;
    to_date: string;
    is_half_day?: boolean;
    fiscal_year?: number;
    is_carry_over?: boolean;
    reason?: string;
  }) =>
    req<{
      id: string;
      status: string;
      detected_conflicts: { kind: string; date: string; label: string }[];
    }>('/leave-requests', { method: 'POST', body: JSON.stringify(body) }),

  // #63 · Mis Ausencias · registra una ausencia (asunto propio / baja médica)
  leaveAbsenceCreate: (body: {
    user_id?: string;
    kind: 'personal' | 'unpaid' | 'compensation_day' | 'sick_leave';
    from_date: string;
    to_date: string;
    is_half_day?: boolean;
    from_time?: string;
    to_time?: string;
    reason?: string;
    justificante_url?: string;
  }) =>
    req<{
      id: string;
      status: string;
      detected_conflicts: { kind: string; date: string; label: string }[];
    }>('/leave-requests', { method: 'POST', body: JSON.stringify(body) }),

  // #63 · sube un justificante a OneDrive y devuelve su URL
  justificanteUpload: (body: { file_name: string; content_base64: string }) =>
    req<{ url: string | null }>('/leave-requests/justificante-upload', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  leaveRequestApprove: (id: string, notes?: string) =>
    req<{ id: string; status: string; calendar_entry_ids: string[] }>(
      `/leave-requests/${id}/approve`,
      {
        method: 'POST',
        body: JSON.stringify(notes ? { notes } : {}),
      },
    ),

  leaveRequestReject: (id: string, notes: string) =>
    req<{ id: string; status: string }>(`/leave-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  // #373 · Teletrabajo · Dirección revoca un día aprobado (libera cupo, avisa a la persona)
  leaveRequestRevoke: (id: string, notes?: string) =>
    req<{ id: string; status: string }>(`/leave-requests/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify(notes ? { notes } : {}),
    }),

  // #373 · Teletrabajo · la persona (o Dirección) cancela su solicitud pending/aprobada
  leaveRequestCancel: (id: string) =>
    req<{ id: string; status: string }>(`/leave-requests/${id}/cancel`, { method: 'POST' }),

  // #373 · Teletrabajo · contexto (cupo X/5, consumo por mes, avisos §5 y eventos del día)
  teletrabajoContext: (params: { user_id?: string; dates: string[]; exclude?: string }) => {
    const q = new URLSearchParams();
    if (params.user_id) q.set('user_id', params.user_id);
    q.set('dates', params.dates.join(','));
    if (params.exclude) q.set('exclude', params.exclude);
    return req<{
      cap: number;
      used_by_month: Record<string, number>;
      days: {
        date: string;
        warnings: { code: string; label: string }[];
        events: { kind: string; label: string }[];
      }[];
    }>(`/leave-requests/teletrabajo/context?${q}`);
  },

  projectsHealth: () =>
    req<{
      items: {
        id: string;
        concept_name: string;
        status: string;
        health_score: number | null;
        health_label: 'healthy' | 'attention' | 'critical' | 'archived';
        track_open: number;
        track_breached: number;
        phases_total: number;
        phases_approved: number;
        selections_total: number;
        selections_final: number;
        annotations_unresolved: number;
      }[];
    }>('/projects/health'),

  projectHealth: (id: string) =>
    req<{
      id: string;
      concept_name: string;
      health_score: number | null;
      health_label: string;
    }>(`/projects/${id}/health`),

  vacationSummary: (user_id?: string, year?: number) => {
    const q = new URLSearchParams();
    if (user_id) q.set('user_id', user_id);
    if (year) q.set('year', String(year));
    return req<{
      contractual_total: number;
      // #402 · false = sin contrato vigente ese año (el total contractual es un relleno, no un
      // dato); el panel lo dice en vez de pintar cifras inventadas. OPCIONAL a propósito: si la
      // respuesta viene de una API anterior a #402 el campo no existe, y ausencia debe leerse
      // como "no lo sé" (⇒ enseñar las cifras), nunca como "no tiene contrato".
      has_contract?: boolean;
      carry_over_available: number;
      consumed_current_year: number;
      consumed_carry_over: number;
      remaining_current_year: number;
      remaining_carry_over: number;
    }>(`/leave-requests/vacation-summary?${q}`);
  },

  // A6 V1.5 · Contactos
  contactsList: (q?: string) => {
    const params = q ? `?q=${encodeURIComponent(q)}` : '';
    return req<{
      items: {
        id: string;
        name: string;
        legal_name: string;
        kind: string;
        email: string | null;
        phone: string | null;
        nif: string | null;
        country_code: string;
        locale: string;
        is_international: boolean;
        fiscal_address: Record<string, string>;
        project_address: Record<string, string>;
        contact_count: number;
        project_count: number;
        created_at: string;
        updated_at: string;
      }[];
    }>(`/contacts${params}`);
  },

  // #352 · ficha de un contacto (fuente de verdad de los datos del lead ligado)
  contactGet: (id: string) =>
    req<{
      id: string;
      name: string;
      legal_name: string;
      email: string | null;
      phone: string | null;
    }>(`/clients/${id}`),

  // #422 · POST/PATCH de clientes invalidan la caché del listado: si no, un cliente recién
  // creado no aparecería en el selector de facturas hasta 2 minutos después.
  contactCreate: (body: Record<string, unknown>) =>
    req<{ id: string }>('/clients', { method: 'POST', body: JSON.stringify(body) }).then((r) => {
      invalidateClientsCache();
      return r;
    }),

  contactUpdate: (id: string, body: Record<string, unknown>) =>
    req<{ id: string; updated: boolean }>(`/clients/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }).then((r) => {
      invalidateClientsCache();
      return r;
    }),

  // A4 · Catálogo de servicios
  serviceCatalogList: (all?: boolean) =>
    req<{
      items: {
        id: string;
        code: string;
        name: string;
        category: string | null;
        default_description: string | null;
        position: number;
        active: boolean;
      }[];
    }>(`/service-catalog${all ? '?all=1' : ''}`),

  serviceCatalogCreate: (body: {
    name: string;
    category?: string;
    default_description?: string;
    position?: number;
  }) =>
    req<{ id: string; code: string }>('/service-catalog', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  serviceCatalogUpdate: (id: string, body: Record<string, unknown>) =>
    req<{ id: string }>(`/service-catalog/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  serviceCatalogDelete: (id: string) =>
    req<{ id: string; deactivated: boolean }>(`/service-catalog/${id}`, { method: 'DELETE' }),

  // #170 p3 · Catálogo de POSICIONES de presupuesto (Fase + Tipo + redacción narrativa).
  // Fuente editable del texto que imprime el PDF; agrupado por plantilla → fase → tipo.
  honorariosPositionCatalog: (template: 'general' | 'amueblado' = 'general') =>
    req<{
      template: string;
      templates: ('general' | 'amueblado')[];
      phases: {
        phase: string;
        phase_num: number;
        phase_label: string;
        positions: {
          phase: string;
          task_type: string;
          code: string;
          label: string;
          narrative: string;
          updated_at: string;
        }[];
      }[];
    }>(`/honorarios/position-catalog?template=${template}`),

  honorariosPositionCatalogSave: (body: {
    template: 'general' | 'amueblado';
    phase: string;
    task_type: string;
    narrative: string;
  }) =>
    req<{ ok: boolean; template: string; phase: string; task_type: string }>(
      '/honorarios/position-catalog',
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  // · #393 · El «cuarteto» de fase de la PROPUESTA WEB, por fase e idioma (ES/EN/DE):
  // Objetivo / Qué contempla / Qué se entrega / Cómo intervenís. Se redacta una vez por
  // tipo de fase y la propuesta lo monta sola.
  proposalPhaseCopy: (locale: 'es' | 'en' | 'de' = 'es') =>
    req<{
      locale: string;
      locales: ('es' | 'en' | 'de')[];
      items: {
        phase: string;
        phase_label: string;
        locale: string;
        objetivo: string;
        contempla: string;
        entrega: string;
        intervencion: string;
        updated_at: string | null;
      }[];
    }>(`/honorarios/proposal-phase-copy?locale=${locale}`),

  proposalPhaseCopySave: (body: {
    phase: string;
    locale: 'es' | 'en' | 'de';
    objetivo: string;
    contempla: string;
    entrega: string;
    intervencion: string;
  }) =>
    req<{ ok: boolean; phase: string; locale: string }>('/honorarios/proposal-phase-copy', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // #170 p4 · Bloques de términos y condiciones que se imprimen al pie del presupuesto.
  honorariosTermsCatalog: () =>
    req<{
      items: { template: string; kind: string; body: string; updated_at: string }[];
    }>('/honorarios/terms-catalog'),

  honorariosTermsCatalogSave: (body: { template: string; kind: string; body: string }) =>
    req<{ ok: boolean; template: string; kind: string }>('/honorarios/terms-catalog', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // A2 · Equipo · config por persona (Dirección)
  teamConfigGet: (userId: string) =>
    req<{
      contract: {
        id: string;
        user_id: string;
        effective_from: string;
        effective_to: string | null;
        termination_date: string | null;
        weekly_hours: number;
        hours_monday: number;
        hours_tuesday: number;
        hours_wednesday: number;
        hours_thursday: number;
        hours_friday: number;
        hours_saturday: number;
        hours_sunday: number;
        annual_vacation_days: number;
        initial_accrued_vacation_days: number;
        role_label: string | null;
        contract_type: string;
        notes: string | null;
        overtime_allowed: boolean;
        out_of_hours_policy: string;
        remote_days_per_month: number;
        schedule_check_in?: Record<string, string> | null;
        schedule_check_out?: Record<string, string> | null;
        schedule_break_start?: Record<string, string> | null;
        schedule_break_end?: Record<string, string> | null;
        fichaje_tolerance_minutes?: number | null;
        gross_monthly_salary: string | null;
        company_cost: string | null;
      } | null;
      documents: {
        id: string;
        doc_kind: string;
        title: string;
        document_url: string | null;
        notes: string | null;
        occurred_on: string;
        created_at: string;
      }[];
    }>(`/team-config/${userId}`),

  teamContractUpsert: (body: Record<string, unknown>) =>
    req<{ id: string }>('/employee-contracts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // V1.5 Feat 5 + 6 · festivos nacionales/locales por miembro + cierres del estudio
  studioHolidaysList: (params?: { year?: number; user_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.year) q.set('year', String(params.year));
    if (params?.user_id) q.set('user_id', params.user_id);
    return req<{
      items: {
        id: string;
        kind: 'national' | 'local' | 'studio_closure';
        date: string;
        label: string;
        region: string | null;
        applies_to_user_id: string | null;
        user_name: string | null;
        deducts_vacation: boolean;
        notes: string | null;
      }[];
    }>(`/studio-holidays?${q}`);
  },
  studioHolidayCreate: (body: {
    kind: 'national' | 'local' | 'studio_closure';
    date: string;
    label: string;
    region?: string;
    applies_to_user_id?: string | null;
    deducts_vacation?: boolean;
    notes?: string;
  }) =>
    req<{ id: string }>('/studio-holidays', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  studioHolidayDelete: (id: string) =>
    req<{ id: string; deleted: true }>(`/studio-holidays/${id}`, { method: 'DELETE' }),
  studioHolidayPatch: (id: string, body: Record<string, unknown>) =>
    req<{ id: string; updated: string[] }>(`/studio-holidays/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  teamDocumentCreate: (
    userId: string,
    body: {
      doc_kind: string;
      title: string;
      document_url?: string;
      notes?: string;
      occurred_on?: string;
    },
  ) =>
    req<{ id: string }>(`/team-config/${userId}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  teamDocumentDelete: (userId: string, docId: string) =>
    req<{ id: string; deleted: boolean }>(`/team-config/${userId}/documents/${docId}`, {
      method: 'DELETE',
    }),

  honorariosListCalculations: (params?: {
    lead_id?: string;
    project_id?: string;
    status?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.lead_id) q.set('lead_id', params.lead_id);
    if (params?.project_id) q.set('project_id', params.project_id);
    if (params?.status) q.set('status', params.status);
    return req<{
      items: {
        id: string;
        parameter_set_version: string;
        lead_id: string | null;
        project_id: string | null;
        model: 'full' | 'consulta';
        typology: string;
        intervention: string;
        area_interior_m2: string;
        area_weighted_m2: string;
        subtotal_cents: string;
        tax_cents: string;
        total_cents: string;
        status: string;
        calc_mode: 'manual_catalog' | 'manual_free' | 'automated';
        pdf_path: string | null;
        created_at: string;
        // #412 · presupuesto vigente del lead + si está fijado a mano + marca de aceptado
        is_current?: boolean;
        current_pinned?: boolean;
        accepted_at?: string | null;
      }[];
    }>(`/honorarios/calculations?${q}`);
  },
  // #412 · «Marcar como vigente» un presupuesto (fija el que gobierna el lead; pin manual)
  honorariosSetCurrent: (id: string, reason?: string) =>
    req<{ ok: boolean; lead_id: string; changed: boolean; from: string | null }>(
      `/honorarios/calculations/${id}/set-current`,
      { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
    ),
  // #412 Fase B · drift ON-READ: artefactos del lead desactualizados vs el presupuesto vigente
  honorariosLeadDrift: (leadId: string) =>
    req<{
      current_calculation_id: string | null;
      any_drift: boolean;
      artifacts: {
        kind: 'contract' | 'proposal' | 'project';
        id: string;
        status?: string;
        drift: boolean;
        reason: string | null;
        action: 'regenerate' | 'anexo' | 'change_order' | null;
      }[];
    }>(`/honorarios/leads/${leadId}/drift`),

  // #346 · cálculo completo (para reabrir wizard / entrada manual con sus datos)
  honorariosGetCalculation: (id: string) =>
    req<{
      id: string;
      lead_id: string | null;
      project_id: string | null;
      client_id: string | null;
      model: 'full' | 'consulta';
      template: 'general' | 'amueblado' | null;
      typology: HonorariosCalcInput['typology'];
      intervention: HonorariosCalcInput['intervention'];
      area_interior_m2: string | null;
      area_exterior_m2: string | null;
      rooms_count: number | null;
      floors_count: number | null;
      low_purchasing_zone: boolean;
      pe_duration_months: string;
      distance_km: string;
      visits_count: number;
      renders: { type: 'estatico' | 'isometrico' | 'panoramico_360' | 'vr'; qty: number }[];
      tasks_snapshot:
        | {
            code: string;
            title: string;
            description?: string;
            required: 'si' | 'no' | 'fijo';
            workload: 'no_computa' | 'muy_baja' | 'baja' | 'media' | 'alta' | 'muy_alta';
          }[]
        | null;
      subtotal_cents: string;
      tax_cents: string;
      total_cents: string;
      status: string;
      entry_mode: 'calculator' | 'manual';
      calc_mode: 'automated' | 'manual_catalog' | 'manual_free';
      manual_breakdown:
        | { concept: string; amount_cents: number | string; notes?: string | null }[]
        | null;
      manual_justification: string | null;
      presupuesto_full_number: string | null;
      // #347 bloque E · redacción editada por partida (para precargar el Paso 4/4 al editar)
      position_overrides: { phase: string; task_type: string; description: string }[] | null;
    }>(`/honorarios/calculations/${id}`),

  // #346 · eliminar presupuesto (la API guarda: nunca firmados ni con contrato/factura ligados)
  honorariosDeleteCalculation: (id: string) =>
    req<{ id: string; deleted: boolean }>(`/honorarios/calculations/${id}`, { method: 'DELETE' }),

  // A4 · Exportar presupuesto PDF (URL absoluta para window.open / <a download>)
  honorariosPdfUrl: (id: string) => `${BASE}/honorarios/calculations/${id}/pdf`,

  // #170 p7 · Verificar presupuesto: asigna número PRE-AAAA-XXXX y quita "Borrador"
  honorariosVerify: (id: string) =>
    req<{ ok: boolean; presupuesto_full_number: string | null }>(
      `/honorarios/calculations/${id}/verify`,
      { method: 'POST' },
    ),

  // #170 p10 · Guardar las líneas de Opciones/Suplidos (fuera del total de honorarios)
  honorariosSaveOpciones: (
    id: string,
    opciones: {
      label: string;
      narrative?: string | null;
      amount_cents?: number | null;
      range_text?: string | null;
    }[],
  ) =>
    req<{ id: string; opciones_count: number }>(`/honorarios/calculations/${id}/opciones`, {
      method: 'PUT',
      body: JSON.stringify({ opciones }),
    }),

  // #170 p4/p6/#163 · Hitos de pago editables por presupuesto (default 70-10-10-10)
  honorariosPaymentPlanOverride: (
    id: string,
    payment_plan: { phase: string; percentage: number; amount_cents: number; due_event: string }[],
    override_reason: string,
  ) =>
    req<{ id: string; override_applied: boolean }>(
      `/honorarios/calculations/${id}/payment-plan-override`,
      { method: 'PATCH', body: JSON.stringify({ payment_plan, override_reason }) },
    ),

  // A4 · Enviar presupuesto al cliente · solo al pulsar el botón (nunca automático)
  honorariosSend: (id: string, to?: string) =>
    req<{ ok: boolean; queued: boolean; sent_to?: string }>(`/honorarios/calculations/${id}/send`, {
      method: 'POST',
      body: JSON.stringify(to ? { to } : {}),
    }),

  // Studio Finance · Gastos (M2.16)
  listExpenses: (params?: {
    category?: string;
    paid?: 'true' | 'false' | 'all';
    year?: number;
    // Ticket A · filtros gasto de proyecto / suplido
    scope?: 'internal' | 'project';
    project_id?: string;
    suplido?: 'true' | 'false';
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.paid) q.set('paid', params.paid);
    if (params?.year) q.set('year', String(params.year));
    if (params?.scope) q.set('scope', params.scope);
    if (params?.project_id) q.set('project_id', params.project_id);
    if (params?.suplido) q.set('suplido', params.suplido);
    if (params?.cursor) q.set('cursor', params.cursor);
    q.set('limit', String(params?.limit ?? 50));
    return req<{
      items: {
        id: string;
        expense_date: string;
        amount_cents: number;
        currency: string;
        tax_rate: number;
        tax_cents: number;
        category: string;
        description: string;
        supplier_name: string | null;
        invoice_ref: string | null;
        document_url: string | null;
        payment_method: string | null;
        due_date: string | null;
        paid: boolean;
        paid_at: string | null;
        // Ticket A · gasto interno/proyecto + suplido + factura adjunta
        scope: 'internal' | 'project';
        project_id: string | null;
        is_suplido: boolean;
        invoice_pdf_url: string | null;
        invoice_pdf_name: string | null;
        // #200 · ¿hay PDF servible (bytes guardados o enlace OneDrive)? → botón "Ver"
        has_pdf: boolean;
        approval_status: 'not_required' | 'pending' | 'approved' | 'rejected';
        approval_request_id: string | null;
        legacy_odoo_id: number | null;
        legacy_odoo_move_type: string | null;
        recurring_template_id: string | null;
        created_at: string;
        // #425 · abonos y rectificativas RECIBIDAS (documento que emite el proveedor).
        // Opcionales a propósito: el front tolera una API que todavía no los sirva
        // (una factura sin document_type se lee como 'invoice', que es lo que es).
        document_type?: ExpenseDocumentType;
        rectifies_expense_id?: string | null;
        rectification_reason?: RectificationReason | null;
        supplier_credit_number?: string | null;
        voided_at?: string | null;
        refund_status?: RefundStatus | null;
        /** Suma de los abonos vinculados a esta factura · EN POSITIVO (0 si no tiene). */
        credited_cents?: number | null;
      }[];
      next_cursor: string | null;
    }>(`/contabilidad/expenses?${q.toString()}`);
  },
  expensesSummary: () =>
    req<{
      totals: {
        total: number;
        unpaid: number;
        amount_total_cents: number;
        amount_unpaid_cents: number;
        amount_year_cents: number;
      };
      by_category: { category: string; count: number; amount_cents: number }[];
    }>('/contabilidad/expenses/summary'),
  createExpense: (body: {
    expense_date: string;
    amount_cents: number;
    currency?: string;
    tax_rate?: number;
    tax_cents?: number;
    category: string;
    description: string;
    supplier_name?: string;
    invoice_ref?: string;
    payment_method?: string;
    due_date?: string;
    paid?: boolean;
    // Ticket A · gasto interno/proyecto + suplido
    scope?: 'internal' | 'project';
    project_id?: string;
    is_suplido?: boolean;
    // #262 · #265 · fiscales
    is_intracomunitario?: boolean;
    retention_rate?: number;
    retention_cents?: number;
    irpf_rate?: number;
    irpf_cents?: number;
  }) =>
    req<{ id: string }>('/contabilidad/expenses', { method: 'POST', body: JSON.stringify(body) }),
  // #259 · forzar la importación de facturas de facturas@munya.studio (además del timer)
  syncInvoicesInbound: () =>
    req<{ processed: number; created: number; errors: string[] }>(
      '/contabilidad/sync-invoices-inbound',
      { method: 'POST' },
    ),
  // #264 · cuentas contables por categoría (para A3)
  // #423 · la fila creció: `status` dice qué pasa con la SUBCUENTA y `lifecycle` qué pasa con la
  // CATEGORÍA en el desplegable (son ejes ortogonales). Todo lo nuevo va OPCIONAL para que el
  // consumidor viejo (GestoriaPanel) siga compilando y funcionando con la respuesta antigua.
  expenseCategoryAccounts: () =>
    req<{
      items: ExpenseCategoryAccount[];
      totals?: {
        total: number;
        assigned: number;
        pending: number;
        not_applicable: number;
        live: number;
      };
    }>('/contabilidad/expense-category-accounts'),
  /**
   * Asigna o quita la subcuenta de una categoría. Poner código CIERRA la pregunta abierta (el
   * backend mueve `status` a 'assigned' y borra `pending_question`). Para devolver una categoría
   * al estado de pendiente hay que mandar el estado y la pregunta explícitamente: el backend
   * rechaza un `pending_decision` sin pregunta con un 400.
   */
  setExpenseCategoryAccount: (
    category: string,
    accounting_code: string | null,
    extra?: {
      status?: ExpenseCategoryAccountStatus;
      pending_question?: ExpenseOpenQuestion | null;
      notes?: string | null;
    },
  ) =>
    req<ExpenseCategoryAccount>(
      `/contabilidad/expense-category-accounts/${encodeURIComponent(category)}`,
      { method: 'PATCH', body: JSON.stringify({ accounting_code, ...extra }) },
    ),

  // #423 · reglas de subcuenta por PROVEEDOR. Ganan a la regla del IRPF y a la subcuenta de la
  // categoría, y son lo que permite al buzón de facturas clasificar sin dejarlo en «Otros».
  expenseSupplierAccounts: (params?: {
    q?: string;
    status?: ExpenseSupplierAccountStatus | 'all';
    active?: 'true' | 'false' | 'all';
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.status) qs.set('status', params.status);
    if (params?.active) qs.set('active', params.active);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{
      items: ExpenseSupplierAccount[];
      totals: { total: number; assigned: number; pending: number; unreviewed: number };
    }>(`/contabilidad/expense-supplier-accounts${suffix}`);
  },
  // 0248 · ficha de UNA regla · es la única llamada que descifra el NIF (una fila ≈ 0,3 s).
  expenseSupplierAccount: (id: string) =>
    req<ExpenseSupplierAccountDetail>(
      `/contabilidad/expense-supplier-accounts/${encodeURIComponent(id)}`,
    ),
  createExpenseSupplierAccount: (body: {
    display_name: string;
    nif?: string | null;
    category?: string | null;
    accounting_code?: string | null;
    pending_question?: ExpenseOpenQuestion | null;
    notes?: string | null;
    is_active?: boolean;
  }) =>
    req<ExpenseSupplierAccount>('/contabilidad/expense-supplier-accounts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateExpenseSupplierAccount: (
    id: string,
    body: Partial<{
      display_name: string;
      nif: string | null;
      category: string | null;
      accounting_code: string | null;
      pending_question: ExpenseOpenQuestion | null;
      notes: string | null;
      is_active: boolean;
    }>,
  ) =>
    req<ExpenseSupplierAccount>(
      `/contabilidad/expense-supplier-accounts/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  // El endpoint responde 204 SIN cuerpo, y `req` siempre hace res.json() → reventaría con un
  // "Unexpected end of JSON input" en el camino feliz. Por eso este va con fetch a pelo.
  deleteExpenseSupplierAccount: async (id: string): Promise<void> => {
    const res = await fetch(
      `${BASE}/contabilidad/expense-supplier-accounts/${encodeURIComponent(id)}`,
      { method: 'DELETE', headers: { 'content-type': 'application/json' } },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { detail?: string; title?: string };
      throw apiError(res.status, body.detail || body.title || `HTTP ${res.status}`);
    }
  },
  // #153 · editar un gasto recibido ya registrado (manual o auto-importado "[REVISAR]")
  updateExpense: (
    id: string,
    body: Partial<{
      expense_date: string;
      amount_cents: number;
      currency: string;
      tax_rate: number;
      tax_cents: number;
      category: string;
      description: string;
      supplier_name: string;
      invoice_ref: string;
      document_url: string;
      payment_method: string;
      due_date: string;
      paid: boolean;
      // Ticket A · mover a proyecto / marcar suplido
      scope: 'internal' | 'project';
      project_id: string | null;
      is_suplido: boolean;
      is_intracomunitario: boolean;
      retention_rate: number;
      retention_cents: number;
      irpf_rate: number;
      irpf_cents: number;
      // #425 · estado del reembolso de un ABONO (en un abono el dinero entra, así que
      // `paid` no dice nada). Se envía por el PATCH de siempre, no hay endpoint propio.
      refund_status: RefundStatus;
    }>,
  ) =>
    req<{ id: string; updated_fields: string[] }>(`/contabilidad/expenses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // ── #425 · abonos y rectificativas RECIBIDAS (las emite el PROVEEDOR) ──
  // El importe y el IVA viajan EN POSITIVO y vuelven EN NEGATIVO: el signo lo pone el
  // backend, nunca el operador. Cada guarda que falle (importe por encima del pendiente,
  // fecha anterior a la factura rectificada, factura ya anulada, periodo cerrado) responde
  // 409 con `detail`, que el helper `req` ya convierte en el mensaje del Error.
  createExpenseCreditNote: (
    id: string,
    body: {
      expense_date: string;
      /** EN POSITIVO, con IVA incluido. */
      amount_cents: number;
      /** EN POSITIVO. */
      tax_cents: number;
      supplier_credit_number: string;
      rectification_reason: RectificationReason;
      notes?: string;
      /** Segundo intento tras el 409 de periodo ya presentado. */
      confirm_prior_period?: boolean;
    },
  ) =>
    req<{
      id: string;
      amount_cents: number; // NEGATIVO
      tax_cents: number; // NEGATIVO
      document_type: 'credit_note';
      rectifies_expense_id: string;
      original_voided: boolean;
    }>(`/contabilidad/expenses/${id}/credit-note`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Vista previa INFORMATIVA del asiento + cuánto queda por abonar. No persiste nada.
  expenseCreditNotePreview: (id: string) =>
    req<{
      pendiente_abonar_cents: number;
      abonos_previos_cents: number;
      asiento: AsientoApiLine[];
    }>(`/contabilidad/expenses/${id}/credit-note-preview`),
  // Ticket A · adjuntar/asociar la factura (PDF base64) de un gasto. Archiva en OneDrive con la
  // nomenclatura AAAA.MM.DD_F.XXX_YYY (+ duplicado en el proyecto si es de proyecto/suplido).
  attachExpenseInvoice: (id: string, body: { pdf_base64: string; ext?: string }) =>
    req<{
      id: string;
      invoice_pdf_name: string;
      invoice_pdf_url: string | null;
      has_pdf: boolean;
      duplicated_to_project: boolean;
      filing_error?: string;
      filing?: string; // #227 · 'pending' = archivado OneDrive en segundo plano
    }>(`/contabilidad/expenses/${id}/attach-invoice`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #200 · "Ver" de una factura RECIBIDA · URL del PDF guardado del gasto (para window.open).
  expenseInvoicePdfUrl: (id: string) => `${BASE}/contabilidad/expenses/${id}/invoice-pdf`,
  markExpensePaid: (id: string) =>
    req<{ id: string; paid: boolean }>(`/contabilidad/expenses/${id}/mark-paid`, {
      method: 'POST',
    }),
  // #367 · marca una factura auto-importada como revisada (quita el marcador [REVISAR])
  markExpenseReviewed: (id: string) =>
    req<{ id: string; reviewed: boolean }>(`/contabilidad/expenses/${id}/mark-reviewed`, {
      method: 'POST',
    }),
  deleteExpense: (id: string) =>
    req<{ id: string; deleted: boolean }>(`/contabilidad/expenses/${id}`, { method: 'DELETE' }),
  // #161 · datos fiscales y bancarios del estudio (CIF, dirección, 2 cuentas)
  fiscalConfig: () =>
    req<{
      fiscal_config: {
        studio_name: string | null;
        legal_name: string | null;
        nif: string | null;
        address: string | null;
        fiscal_address_header: string | null;
        fiscal_address_footer: string | null;
        iban: string | null;
        bic: string | null;
        bank_name: string | null;
        iban_suplidos: string | null;
        bic_suplidos: string | null;
        bank_name_suplidos: string | null;
      } | null;
    }>('/contabilidad/fiscal-config'),
  // #157 · acciones en lote sobre gastos recibidos (marcar pagada / borrar)
  bulkExpenses: (ids: string[], action: 'mark_paid' | 'delete') =>
    req<{
      action: string;
      requested: number;
      affected: number;
      skipped: { id: string; reason: string }[];
    }>('/contabilidad/expenses/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action }),
    }),

  chatCreateConversation: (body: {
    kind: 'channel' | 'dm' | 'group_dm';
    name?: string;
    description?: string;
    participant_user_ids?: string[];
    is_private?: boolean;
  }) =>
    req<{ id: string }>('/chat/conversations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  search: (q: string, ai = false, limit = 20) => {
    const p = new URLSearchParams({ q, limit: String(limit), ai: ai ? '1' : '0' });
    return req<{
      items: {
        kind: 'lead' | 'client' | 'invoice';
        id: string;
        title: string;
        subtitle: string;
        url: string;
        score: number;
      }[];
      query: string;
      ai_reranked: boolean;
    }>(`/search?${p}`);
  },

  pipelineSummary: () =>
    req<{
      summary: {
        stage: LeadStage;
        count: number;
        total_budget_cents: number;
        avg_budget_cents: number | null;
      }[];
      conversion: {
        month: string;
        new_count: number;
        won_count: number;
        conversion_rate: number;
      }[];
    }>('/pipeline/summary'),

  // Almacén · OneDrive vía Graph
  almacenTree: (params?: { path?: string; item_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.path) q.set('path', params.path);
    if (params?.item_id) q.set('item_id', params.item_id);
    return req<{
      items: AlmacenItem[];
      path: string;
    }>(`/almacen/tree?${q.toString()}`);
  },
  // Crea una sesión de subida resumable; el navegador sube el archivo directo a upload_url (OneDrive).
  // target: folder_id (Documentos del proyecto) o path (explorador Almacén).
  almacenUploadSession: (opts: { folder_id?: string; path?: string; name: string }) =>
    req<{ upload_url: string; expires: string | null }>('/almacen/upload-session', {
      method: 'POST',
      body: JSON.stringify(opts),
    }),
  almacenRecent: () => req<{ items: AlmacenItem[] }>(`/almacen/recent`),
  // #459 · `path` acota la búsqueda a una rama. Cada resultado vuelve con `parent_path`,
  // la carpeta donde vive, porque un resultado de búsqueda sin ruta no se puede abrir.
  almacenSearch: (q: string, path?: string) => {
    const p = new URLSearchParams({ q });
    if (path) p.set('path', path);
    return req<{ items: AlmacenItem[]; path: string; scoped: boolean }>(
      `/almacen/search?${p.toString()}`,
    );
  },

  // ─── Trust Accounts (Suplidos) · M3.6 ───────────────────────────
  trustAccount: (projectId: string) =>
    req<{ account: TrustAccount; movements: TrustMovement[] }>(
      `/projects/${projectId}/trust-account`,
    ),
  // #249 #250 · suplidos emitidos
  issuedSuplidosForProject: (projectId: string) =>
    req<{ items: IssuedSuplido[] }>(`/projects/${projectId}/issued-suplidos`),
  issuedSuplidosGlobal: () => req<{ items: IssuedSuplido[] }>(`/issued-suplidos`),
  issuedSuplidoDetail: (id: string) =>
    req<{
      suplido: IssuedSuplido;
      lines: IssuedSuplidoLine[];
      branding: IssuedSuplidoBranding | null;
    }>(`/issued-suplidos/${id}`),
  createIssuedSuplido: (projectId: string, body: { item_ids: string[]; notes?: string }) =>
    req<{ id: string; suplido_number: string; total_cents: number }>(
      `/projects/${projectId}/issued-suplidos`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  emitIssuedSuplido: (id: string) =>
    req<{ id: string; status: string }>(`/issued-suplidos/${id}/emit`, { method: 'POST' }),
  cancelIssuedSuplido: (id: string) =>
    req<{ id: string; cancelled: boolean }>(`/issued-suplidos/${id}`, { method: 'DELETE' }),
  createTrustMovement: (body: CreateTrustMovementBody) =>
    req<{ id: string }>(`/trust-movements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteTrustMovement: (id: string) =>
    req<{ id: string; deleted: true }>(`/trust-movements/${id}`, { method: 'DELETE' }),
  settleTrustAccount: (projectId: string) =>
    req<{ invoice_id: string; final_amount_cents: number; full_number: string }>(
      `/projects/${projectId}/trust-account/settle`,
      { method: 'POST' },
    ),

  // ─── Selección · bloque A 2026-06-10 (#115 #92 #100) ─────────────────────
  // POST /selection/items/:id/alternative · crea alternativa colgando de la principal
  createSelectionAlternative: (parentId: string, body: Partial<SelectionItemCreate>) =>
    req<{ id: string; identifier: string }>(`/selection/items/${parentId}/alternative`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #195 · "swap": promueve una alternativa a principal (identificadores congelados)
  // · #452 §1 · `keep_link_ids` = referencias que el operador decide DEJAR en la pieza
  // antigua. Sin ese campo, todas viajan al nuevo principal.
  promoteSelectionItem: (altId: string, body?: { keep_link_ids?: string[] }) =>
    req<{ id: string; promoted: boolean; references?: SelectionRepointSummary }>(
      `/selection/items/${altId}/promote`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  // · #452 §1 · qué referencias arrastraría el intercambio (para el diálogo). Sin efectos.
  promoteSelectionPreview: (altId: string) =>
    req<{
      parent: { id: string; identifier: string; title: string } | null;
      links: SelectionRepointCandidate[];
    }>(`/selection/items/${altId}/promote-preview`),
  // ── #474 §1 · borrado en masa ────────────────────────────────────────────
  // La previsualización y la ejecución mandan el MISMO cuerpo: el servidor recalcula el
  // alcance al confirmar, así que la pantalla no tiene que acordarse de nada entre las dos.
  bulkDeleteSelectionPreview: (
    projectId: string,
    body: { item_ids: string[]; alternatives?: 'delete' | 'orphan' },
  ) =>
    req<{
      principals: number;
      alternatives: number;
      dragged: number;
      blocked: { id: string; identifier: string; title: string; reason: string }[];
    }>(`/projects/${projectId}/selection/bulk-delete-preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  bulkDeleteSelection: (
    projectId: string,
    body: { item_ids: string[]; alternatives: 'delete' | 'orphan' },
  ) =>
    req<{
      deleted: number;
      orphaned: number;
      blocked: { id: string; identifier: string; title: string; reason: string }[];
    }>(`/projects/${projectId}/selection/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // ── #474 §2 · traslado lateral de una alternativa ────────────────────────
  selectionMoveTargets: (projectId: string, excludeId: string, q: string) =>
    req<{ targets: SelectionMoveTarget[] }>(
      `/projects/${projectId}/selection/move-targets?exclude=${encodeURIComponent(excludeId)}&q=${encodeURIComponent(q)}`,
    ),
  selectionMovePreview: (itemId: string, toParentId: string) =>
    req<SelectionMovePreview>(
      `/selection/items/${itemId}/move-preview?to=${encodeURIComponent(toParentId)}`,
    ),
  moveSelectionAlternative: (
    itemId: string,
    body: { to_parent_id: string; inherit_space: boolean },
  ) =>
    req<{
      id: string;
      from_identifier: string;
      to_identifier: string;
      parent_identifier: string;
      moved_alternatives: number;
    }>(`/selection/items/${itemId}/move`, { method: 'POST', body: JSON.stringify(body) }),
  // · #452 §3 · catálogos de estancia tipo (categorías + ubicaciones canónicas)
  selectionRoomCatalog: () =>
    req<{ groups: RoomGroupCatalogRow[]; locations: LocationCatalogRow[] }>(
      '/selection/room-catalog',
    ),
  createRoomGroup: (label: string) =>
    req<{ code: string; label: string }>('/selection/room-catalog/groups', {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),
  updateRoomGroup: (code: string, body: { label?: string; position?: number; active?: boolean }) =>
    req<{ code: string }>(`/selection/room-catalog/groups/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteRoomGroup: (code: string) =>
    req<{ code: string; deleted: boolean; deactivated?: boolean; spaces?: number }>(
      `/selection/room-catalog/groups/${encodeURIComponent(code)}`,
      { method: 'DELETE' },
    ),
  createCatalogLocation: (body: {
    label: string;
    room_group?: string | null;
    max_instances?: number;
  }) =>
    req<{ code: string; label: string }>('/selection/room-catalog/locations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateCatalogLocation: (
    code: string,
    body: {
      label?: string;
      room_group?: string | null;
      max_instances?: number;
      position?: number;
      active?: boolean;
    },
  ) =>
    req<{ code: string }>(`/selection/room-catalog/locations/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteCatalogLocation: (code: string) =>
    req<{ code: string; deleted: boolean; deactivated?: boolean; spaces?: number }>(
      `/selection/room-catalog/locations/${encodeURIComponent(code)}`,
      { method: 'DELETE' },
    ),
  // · #399 p6 · alta de imagen server-side: el API resuelve el downloadUrl de Graph,
  // baja los bytes con límites y los mete por el pipeline (el webUrl de SharePoint es
  // un visor HTML, no una imagen). item_id = id del DriveItem que entrega el picker.
  selectionMediaFromAlmacen: (
    itemId: string,
    body: { item_id: string; is_primary?: boolean; caption?: string },
  ) =>
    req<SelectionMediaCreated>(`/selection/items/${itemId}/media/from-almacen`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #399 p6 · subida directa (JSON base64, convención de la casa; máx 20 MB de imagen)
  selectionMediaUpload: (
    itemId: string,
    body: { filename: string; content_base64: string; is_primary?: boolean; caption?: string },
  ) =>
    req<SelectionMediaCreated>(`/selection/items/${itemId}/media/upload`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #483 · elegir la portada del artículo. El servidor garantiza que solo queda una
  // principal (índice único parcial de la 0056) y rechaza con 409 una foto sin procesar.
  selectionMediaSetPrimary: (mediaId: string) =>
    req<{ id: string; primary: SelectionMediaPrimary }>(`/selection/media/${mediaId}/primary`, {
      method: 'POST',
    }),
  // · #483 · guardar el orden de la galería · va la lista COMPLETA en el orden deseado
  selectionMediaReorder: (itemId: string, mediaIds: string[]) =>
    req<{ ok: boolean; media_ids: string[]; primary: SelectionMediaPrimary }>(
      `/selection/items/${itemId}/media/order`,
      { method: 'PUT', body: JSON.stringify({ media_ids: mediaIds }) },
    ),
  // #100 · referencias cruzadas entre artículos
  selectionItemLinks: (itemId: string) =>
    req<{ items: SelectionItemLink[] }>(`/selection/items/${itemId}/links`),
  addSelectionItemLink: (
    itemId: string,
    body: { to_item_id: string; link_kind?: SelectionLinkKind; note?: string },
  ) =>
    req<{ id: string }>(`/selection/items/${itemId}/links`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteSelectionItemLink: (linkId: string) =>
    req<{ ok: boolean }>(`/selection/links/${linkId}`, { method: 'DELETE' }),

  // #191 · Flujo de revisión por versiones (lado equipo)
  selectionReviewState: (projectId: string) =>
    req<SelectionReviewState>(`/projects/${projectId}/selection-review`),
  selectionReviewHistory: (projectId: string) =>
    req<{ items: SelectionReviewHistoryRow[] }>(`/projects/${projectId}/selection-review/history`),
  selectionReviewItems: (reviewId: string) =>
    req<{ items: SelectionReviewSnapshotItem[] }>(`/selection-review/${reviewId}/items`),
  startSelectionReview: (projectId: string) =>
    req<{ review_id: string; seq: number; items: number }>(
      `/projects/${projectId}/selection-review/start`,
      { method: 'POST' },
    ),
  finalSelectionReview: (projectId: string) =>
    req<{ review_id: string; seq: number; items: number }>(
      `/projects/${projectId}/selection-review/final`,
      { method: 'POST' },
    ),
  changesSelectionReview: (projectId: string) =>
    req<{ review_id: string; seq: number; items: number }>(
      `/projects/${projectId}/selection-review/changes`,
      { method: 'POST' },
    ),
  // #441 · anular una ronda enviada (prerrequisito para poder reconstruir un proyecto)
  selectionReviewRevokePreview: (projectId: string) =>
    req<SelectionReviewRevokePreview>(`/projects/${projectId}/selection-review/revoke-preview`),
  revokeSelectionReview: (projectId: string, reviewId: string, reason: string) =>
    req<{
      review_id: string;
      seq: number;
      type: SelectionReviewType;
      items: number;
      decisions_kept: number;
      process_state: SelectionProcessState;
    }>(`/projects/${projectId}/selection-review/revoke`, {
      method: 'POST',
      body: JSON.stringify({ review_id: reviewId, reason }),
    }),
  // · #438 §3 · detalle previo al envío (qué sale, qué se queda, cuánto importe, qué falla)
  selectionStartReviewPreview: (projectId: string) =>
    req<SelectionStartReviewPreview>(`/projects/${projectId}/selection-review/start-preview`),
  // · #438 §1 · ocultar/mostrar posiciones al cliente. Un solo endpoint para la ficha, el
  // menú de fila y la selección múltiple. `reason` es obligatorio al ocultar.
  setSelectionVisibility: (
    projectId: string,
    body: { item_ids: string[]; visibility: SelectionClientVisibility; reason?: string },
  ) =>
    req<SelectionVisibilityResult>(`/projects/${projectId}/selection/visibility`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #438 §4 · aprobación INTERNA de Dirección sobre posiciones OCULTAS (arranca compras).
  // No escribe la aprobación del cliente y no abre suplidos: ver visibility.ts en el API.
  internalApproveSelection: (
    projectId: string,
    body: { item_ids: string[]; note?: string; approve?: boolean },
  ) =>
    req<SelectionInternalApproveResult>(`/projects/${projectId}/selection/internal-approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #449 §1 · «La compro yo» · quién compra las posiciones. Un solo endpoint para la ficha,
  // el menú de fila, la selección múltiple y los ATAJOS (`scope`: una estancia o una
  // categoría enteras). `note` es obligatoria al excluir y NO es interna: sale en el acta.
  setSelectionPurchaseBy: (
    projectId: string,
    body: {
      item_ids?: string[];
      scope?: { space_id?: string | null; category?: string };
      purchase_by: SelectionPurchaseBy;
      note?: string;
    },
  ) =>
    req<SelectionPurchaseByResult>(`/projects/${projectId}/selection/purchase-by`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #470 §7 · marcar posiciones como APORTADAS POR EL CLIENTE (o devolverlas a gestión del
  // estudio). DOS PASADAS con la misma respuesta: sin `confirm` el servidor devuelve el plan
  // —qué se limpia, qué está bloqueado y qué avisos hay— y no escribe nada. Se llama dos
  // veces a propósito: la conversión BORRA importes, y eso se enseña antes de hacerlo.
  setSelectionProcedencia: (
    projectId: string,
    body: { item_ids: string[]; procedencia: SelectionProcedencia; confirm?: boolean },
  ) =>
    req<SelectionProcedenciaPlan>(`/projects/${projectId}/selection/procedencia`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // · #470 §4 · proponer la retirada de una pieza que el cliente ya tiene, o cancelar una
  // propuesta que aún no ha contestado. El motivo lo escribe el estudio y lo LEE el cliente.
  setSelectionRetirada: (
    itemId: string,
    body: { action: 'proponer' | 'cancelar'; motivo?: string },
  ) =>
    req<{ id: string; retirada_estado: SelectionRetiradaEstado | null; retirada_motivo?: string }>(
      `/selection/items/${itemId}/retirada`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  markSelectionChangePending: (itemId: string, pending: boolean, note?: string) =>
    req<{ id: string; change_pending: boolean }>(`/selection/items/${itemId}/mark-change-pending`, {
      method: 'POST',
      body: JSON.stringify(note != null ? { pending, note } : { pending }),
    }),
  // · #482 · histórico de cambios sobre una posición aprobada (bloque «Cambios desde la
  // aprobación» de la ficha): motivo, antes/después por campo, quién y si el cliente ya lo sabe.
  selectionItemChanges: (itemId: string) =>
    req<{ changes: SelectionItemChange[] }>(`/selection/items/${itemId}/changes`),
  // · #482 · levantar la congelación. NO borra la aprobación del cliente: libera la pieza para
  // las operaciones estructurales (borrarla, moverla de familia) que se saltan las congeladas.
  unfreezeSelectionItem: (itemId: string, reason: string) =>
    req<{ id: string; frozen_at: null }>(`/selection/items/${itemId}/unfreeze`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

/** · #482 · una entrada del registro de cambios sobre una posición ya aprobada. */
export interface SelectionItemChangeField {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

export interface SelectionItemChange {
  id: string;
  /** el motivo que escribió el equipo · es el texto que lee el CLIENTE */
  reason: string;
  fields: SelectionItemChangeField[];
  changed_at: string;
  changed_by_name?: string | null;
  changed_by_email?: string | null;
  changed_by_role?: string | null;
  /** null = el cliente todavía no lo sabe (entrará en la próxima ronda) */
  communicated_at?: string | null;
  communicated_review_seq?: number | null;
}

export type TrustMovementKind =
  | 'client_advance'
  | 'provider_payment'
  | 'fee_invoice_settle'
  | 'final_suplido_invoice'
  | 'adjustment';

export interface IssuedSuplido {
  id: string;
  suplido_number: string;
  status: string;
  issue_date: string;
  subtotal_cents?: number;
  tax_cents?: number;
  total_cents: number;
  notes?: string | null;
  project_id?: string;
  project_name?: string | null;
  created_at?: string;
}
export interface IssuedSuplidoLine {
  id: string;
  identifier: string | null;
  title: string;
  quantity: string | number;
  // · #466 · unidad y PRECIO UNITARIO de la línea. El acta separa «Unitario» de «Total»
  //   (la cabecera «Importe» contenía el total y se leía como precio por unidad). En las
  //   líneas emitidas antes de 0264 el unitario lo deriva el API desde el propio documento.
  unit: string | null;
  total_cents: number;
  unit_cents: number | null;
  position: number;
}
export interface IssuedSuplidoBranding {
  studio_name: string | null;
  legal_name: string | null;
  nif: string | null;
  iban_suplidos: string | null;
  bic_suplidos: string | null;
  bank_name_suplidos: string | null;
  fiscal_address_footer: string | null;
}

export interface TrustAccount {
  id: string;
  project_id: string;
  balance_cents: number;
  minimum_balance_cents: number;
  settled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TrustMovement {
  id: string;
  kind: TrustMovementKind;
  amount_cents: number;
  occurred_at: string | null;
  description: string;
  counterparty_name: string | null;
  invoice_id: string | null;
  bank_reference: string | null;
  document_url: string | null;
  created_at: string;
  created_by: string | null;
  // #228 · 'expense' = gasto recibido marcado suplido (read-only); 'movement' = movimiento real
  source?: 'movement' | 'expense';
}

export interface RecurringExpense {
  id: string;
  name: string;
  category: string;
  description: string;
  supplier_name: string | null;
  amount_cents: number;
  tax_rate: number;
  currency: string;
  frequency: 'monthly' | 'quarterly' | 'annual';
  day_of_month: number | null;
  month_of_year: number | null;
  active: boolean;
  next_due_date: string;
  notes: string | null;
  created_at: string;
}

export interface BankStatementRow {
  id: string;
  bank_account_iban_hash: string;
  period_start: string;
  period_end: string;
  initial_balance_cents: number;
  final_balance_cents: number;
  source_format: string;
  source_filename: string | null;
  imported_at: string;
  lines_count: number;
}

export interface BankStatementLine {
  id: string;
  line_number: number;
  operation_date: string;
  value_date: string;
  amount_cents: number;
}

export interface ProviderHistoryRow {
  provider_name: string;
  total_pos: number;
  received_pos: number;
  cancelled_pos: number;
  avg_days_sent_to_received: number | null;
  first_po_at: string | null;
  last_po_at: string | null;
  total_volume_cents: number;
  open_incidents: number;
}

export interface RemateItem {
  id: string;
  project_id: string;
  sequence_number: number;
  subject: string;
  description: string | null;
  space_id: string | null;
  space_label: string | null;
  project_name: string | null;
  responsible_name: string | null;
  responsible_email: string | null;
  originating_track_item_id: string | null;
  excel_ref: string | null;
  status: 'open' | 'in_progress' | 'blocked' | 'verified' | 'closed';
  photo_url: string | null;
  photo_after_url: string | null;
  estimated_resolution_date: string | null;
  actual_resolution_date: string | null;
  cost_cents: number;
  created_at: string;
}

export interface CreateTrustMovementBody {
  project_id: string;
  kind: TrustMovementKind;
  amount_cents: number;
  description: string;
  counterparty_name?: string;
  bank_reference?: string;
  document_url?: string;
  occurred_at?: string;
}

export interface AlmacenItem {
  id: string;
  name: string;
  kind: 'folder' | 'file';
  size?: number;
  modified?: string;
  child_count?: number;
  mime_type?: string;
  web_url?: string;
  path?: string;
  /** #459 · solo en resultados de búsqueda: carpeta contenedora, p.ej. "/MUNYA OS/PROYECTOS/026_…" */
  parent_path?: string;
}

// ============================================================================
// Cuentas nativas del portal cliente (decisión Jorge 3-jul · flag portal_native_accounts)
// El magic link pasa a ser el canal de INVITACIÓN: al consumirlo, si el cliente
// aún no tiene cuenta se le obliga a crearla (email+contraseña); los accesos
// siguientes son por POST /auth/portal-login desde la pantalla de login.
// ============================================================================

// Longitud mínima de contraseña de cuenta de portal (contrato: 10 caracteres)
export const PORTAL_PASSWORD_MIN = 10;

// POST /api/v1/portal/redeem · respuesta del canje del magic link.
// · Con cuenta ya creada (o flag OFF): emite la sesión como hoy → PortalRedeemOk.
// · Con flag ON y SIN cuenta: NO emite sesión → needs_account_setup + setup_token
//   efímero (TTL 30 min) para POST /api/v1/portal/setup-account.
export interface PortalRedeemOk {
  ok: true;
  needs_account_setup?: false; // discriminante · ausente en el shape actual
  portal_kind: 'client' | 'constructor' | 'partner';
  project_id: string | null;
  target_email: string;
  language: 'es' | 'en' | 'de';
}
export interface PortalRedeemNeedsSetup {
  needs_account_setup: true;
  setup_token: string;
  email: string; // email de la cuenta a crear (visible NO editable en el formulario)
}
export type PortalRedeemResponse = PortalRedeemOk | PortalRedeemNeedsSetup;

// POST /api/v1/portal/setup-account · crea la cuenta nativa y emite la sesión de portal
export interface PortalSetupAccountBody {
  setup_token: string;
  password: string;
  /**
   * #446.5 · RGPD · versión del aviso de privacidad que la pantalla de alta acaba de ENSEÑAR.
   * El servidor solo deja constancia de «informado» si esto llega y coincide con la versión
   * vigente. Sin ella la cuenta se crea igual, pero el aviso queda pendiente y el registro de
   * actividad no escribe nada de esa persona hasta que lo vea.
   */
  privacy_notice_version?: string;
}

// POST /auth/portal-login · login de cliente con cuenta nativa.
// 401 NEUTRO (mismo error y mismo tiempo para email inexistente y contraseña mala).
export interface PortalLoginBody {
  email: string;
  password: string;
}
export interface PortalLoginResponse {
  ok: true;
  project_id: string | null; // proyecto de la cookie emitida (el más reciente si hay varios)
}

// POST /auth/portal-reset-request {email} · respuesta neutra SIEMPRE (200 exista o no la cuenta)
export interface PortalResetRequestBody {
  email: string;
}
// POST /auth/portal-reset · restablece contraseña con el token del email (TTL 1h, un solo uso)
// y desbloquea la cuenta (failed_attempts/locked_at a cero).
export interface PortalResetBody {
  token: string;
  password: string;
}

// GET /api/v1/portal/my-projects · proyectos del client_id de la cuenta (solo si la
// sesión procede de cuenta nativa; con sesión de magic link responde error → sin selector).
export interface PortalMyProject {
  id: string;
  concept_name: string | null;
  status?: string;
  is_current?: boolean;
  /** #445 · papel con el que la identidad entra a ESE proyecto (cliente | constructora). */
  role?: string;
}
export interface PortalMyProjectsResponse {
  items: PortalMyProject[];
}
// POST /api/v1/portal/switch-project {project_id} · #445.5 · MUEVE la sesión al proyecto pedido
// (UPDATE de portal_sessions), sin reemitir cookie y sin reautenticarse. Solo acepta proyectos
// con acceso vivo de la identidad, verificado en BBDD. La lista de proyectos ya no depende del
// client_id de la cuenta: una persona con dos fichas ve los dos con un solo login.
export interface PortalSwitchProjectBody {
  project_id: string;
}

// GET /api/v1/portal/my-projects/summary · #481 · lo mismo que my-projects MÁS lo que hace falta
// para pintar una tarjeta de elección al entrar (tipología, ubicación, fase en curso y lo que
// espera por esa persona). Los contadores salen de la misma fuente que GET /portal/pending, que
// es lo que garantiza que la tarjeta no prometa un pendiente que dentro no existe.
//
// ⚠️ `pending`, `moodboard_image_url` y las fases llegan a null cuando el acceso a ESE proyecto
// no es de cliente (la misma identidad puede ser constructora en otra obra): no es que no haya
// nada pendiente, es que no se ha mirado. «Todo al día» solo se escribe con `pending` presente.
export interface PortalProjectCard {
  id: string;
  concept_name: string | null;
  role?: string;
  status?: string;
  is_current?: boolean;
  typology: string | null;
  city: string | null;
  area_interior_m2: string | null;
  moodboard_image_url: string | null;
  /** Valor del enum `project_phase` (el rótulo lo pone el portal, que ya tiene el suyo). */
  current_phase: string | null;
  current_phase_position: number | null;
  phases_total: number | null;
  phases_done: number | null;
  pending: {
    selection: number;
    signatures: number;
    visits: number;
    invoices: number;
    feedback: number;
    total: number;
  } | null;
}
export interface PortalProjectCardsResponse {
  items: PortalProjectCard[];
}
